// Mind2Magic CRM — DOCX Itinerary Generator (Magic Planner 1.5)
// Generates editable DOCX in Sara's voice using lead data + content blocks.
// Uses `docx` npm package (lazy-loaded). Sara edits in Word/Google Docs, re-uploads.

import { store } from '../store.js'
import {
  GREETING, LIGHTNING_LANE, DISNEY_SPRINGS, CHECK_IN_DAY,
  PARK_RIDES, PARK_SHOWS, PARK_CHARACTERS, PARK_RESTAURANTS,
  MEAL_PLAN, INFO_ADICIONAL, SHOPPING, EPIC_UNIVERSE,
  UNIVERSAL_PARKS, CITY_WALK, SECTION_MARKERS,
  SINGLE_RIDER, REST_DAY, CHECKOUT_DAY,
  SKYLINER_HOTELS, SKYLINER_EPCOT_TIP,
  HOTEL_TRANSITION,
  PARK_ENTERTAINMENT, PARK_NARRATIVE, HOTEL_DINING_RECS, EXPERIENCE_LABELS,
  generateChildNotes, classifyHotel, AGE_TO_HEIGHT,
} from './content-blocks.js'

// Use pre-built IIFE bundle loaded via <script> tag in index.html.
// This BYPASSES Vite/Rollup bundling which reorders class declarations
// inside the docx npm package, causing TDZ errors ("can't access lexical
// declaration 'j' before initialization").
function loadDocx() {
  if (!window.docx) {
    throw new Error('docx IIFE bundle not loaded. Ensure <script src="/vendor/docx.iife.js"> is in index.html.')
  }
  return window.docx
}

// ── MAIN EXPORT: Generate DOCX for a lead ────────────────────────────────

export async function generateItineraryDocx(lead, config = {}) {
  const D = await loadDocx()
  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
    HeadingLevel, AlignmentType, WidthType, BorderStyle,
    CheckBox, ShadingType, TableLayoutType,
  } = D

  const familyName = config.familyName || extractFamilyName(lead)
  const viajeros = lead.grupoViajeros || lead.formData?.grupoViajeros || []
  const parks = config.parks || detectParks(lead)
  const hasDining = config.hasDining ?? detectDiningPlan(lead)
  const diningPlanType = config.diningPlanType || detectMealPlanType(lead)
  const isDisney = parks.some(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  const isUniversal = parks.some(p => ['uso', 'ioa', 'epic'].includes(p))

  // Guide form responses — personalize sections when available
  const gr = lead.formData?.guideResponses || {}
  const hasGuideData = Object.keys(gr).length > 0

  // Detect "no coasters" preference from traveler's vision/preferences
  const noCoasters = detectNoCoasters(gr)

  // Hotel classification — early entry, extended hours, Express Pass
  const hotelInfo = config.hotelInfo || classifyHotel(lead)
  const hotelName = hotelInfo.hotelName || getFirstHotel(lead)

  // Live closures/refurbishments per park (from backend API)
  const closures = config.closures || {}

  // Build sections
  const sections = []

  // ── Section content tracker ── tracks plain text per section for diffing later
  // (extractSectionContent can't traverse docx Paragraph internals reliably)
  const _sectionTexts = {}
  let _currentSectionKey = 'header'

  // Hidden marker helper — white 1pt text, invisible in Word but parseable by mammoth
  const _origMarker = (text) => new Paragraph({
    children: [new TextRun({ text, size: 2, color: 'FFFFFF', font: 'Arial' })],
    spacing: { after: 0, before: 0 },
  })
  const marker = (text) => {
    // Update current section key when a marker is added
    const match = text.match(/\{\{M2M_SECTION:([^}]+)\}\}/)
    if (match) {
      _currentSectionKey = match[1]
      _sectionTexts[_currentSectionKey] = ''
    }
    return _origMarker(text)
  }

  // Wrap sections.push to also track text content per section
  // docx npm stores text in XML tree: TextRun.root[1] (w:t) → root.filter(string)
  const _origPush = sections.push.bind(sections)
  function _extractDocxText(obj) {
    if (typeof obj === 'string') return obj
    if (Array.isArray(obj)) return obj.map(_extractDocxText).join('')
    if (obj && obj.rootKey === 'w:t') {
      return (obj.root || []).filter(r => typeof r === 'string').join('')
    }
    if (obj && obj.rootKey === 'w:r') {
      return (obj.root || []).map(_extractDocxText).join('')
    }
    if (obj && obj.root) return _extractDocxText(obj.root)
    return ''
  }
  sections.push = function (...items) {
    for (const item of items) {
      if (!item || !item.root) continue
      const text = _extractDocxText(item)
      if (text && !text.includes('{{M2M_SECTION:')) {
        _sectionTexts[_currentSectionKey] = (_sectionTexts[_currentSectionKey] || '') + text + '\n'
      }
    }
    return _origPush(...items)
  }

  // ── GREETING ──
  sections.push(marker(SECTION_MARKERS.greeting))
  sections.push(...buildGreeting(familyName, isDisney, isUniversal, { Paragraph, TextRun, HeadingLevel, AlignmentType }))

  // ── SARA'S PERSONAL NOTE (from guide form visionViaje / notasSara) ──
  if (gr.visionViaje || gr.notasSara) {
    sections.push(...buildPersonalNote(gr, familyName, { Paragraph, TextRun, HeadingLevel }))
  }

  // ── RESERVATION CODES ──
  const resCodes = buildReservationCodes(lead, { Paragraph, TextRun })
  if (resCodes.length > 0) {
    sections.push(...resCodes)
  }

  // ── AGENDA ──
  sections.push(marker(SECTION_MARKERS.agenda))
  sections.push(...buildAgenda(lead, parks, config, { Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType, TableLayoutType }))

  // ── LIGHTNING LANE (Disney only — skip if ALL parks chose 'no') ──
  if (isDisney) {
    const anyLL = !hasGuideData || parks.some(p => {
      const choice = gr[`ll_${p}`]
      return !choice || choice !== 'no'
    })
    if (anyLL) {
      sections.push(marker(SECTION_MARKERS.lightningLane))
      sections.push(...buildLightningLane(parks, hotelInfo, lead, { Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, ShadingType, AlignmentType, TableLayoutType }))
    }
  }

  // ── DISNEY SPRINGS (Disney only) ──
  if (isDisney) {
    sections.push(marker(SECTION_MARKERS.disneySprings))
    sections.push(...buildDisneySprings(gr, { Paragraph, TextRun, HeadingLevel }))
  }

  // ── CHECK-IN DAY ──
  sections.push(marker(SECTION_MARKERS.checkIn))
  sections.push(...buildCheckInDay(lead, hotelInfo, config, { Paragraph, TextRun, HeadingLevel }))

  // ── CELEBRATIONS (from guide form + auto "Primera vez") ──
  sections.push(...buildCelebrations(gr, familyName, isDisney, isUniversal, { Paragraph, TextRun, HeadingLevel }))

  // ── PARK DAYS ──
  const parkSchedule = config.parkSchedule || buildDefaultSchedule(lead, parks)

  // Track previous date to detect gap/rest days
  let prevDate = null
  const checkInDateStr = config.checkInDate || lead.hotels?.[0]?.checkIn || lead.travelDate || ''
  if (checkInDateStr) prevDate = checkInDateStr

  for (let i = 0; i < parkSchedule.length; i++) {
    const day = parkSchedule[i]
    const parkCode = day.parkCode

    // Insert rest day sections for gap days
    if (prevDate && day.date) {
      let gapDate = addOneDayISO(prevDate)
      while (gapDate < day.date) {
        sections.push(new Paragraph({
          text: `${formatDateLabel(gapDate)}: Descanso ${hotelName} + Disney Springs`,
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
        }))
        sections.push(...buildRestDay(lead, hotelInfo, gapDate, config, { Paragraph, TextRun, HeadingLevel }))
        gapDate = addOneDayISO(gapDate)
      }
    }
    prevDate = day.date

    sections.push(marker(SECTION_MARKERS.parkDay(parkCode, i + 1)))

    // Park hours — enhanced with hotel tier info (calculated before heading so we can include hours in heading)
    const dayHours = getEnhancedHours(day, parkCode, hotelInfo, config.parkHoursData)

    // Build Sara's heading format: "Marzo 11: Animal Kingdom (7:30 am a 6:00 pm)"
    const parkHoursDisplay = dayHours?.display || ''
    const parkHeadingText = day.dateLabel
      ? `${day.dateLabel}: ${getParkName(parkCode)}${parkHoursDisplay ? ` (${parkHoursDisplay})` : ''}`
      : `Día ${i + 1}: ${getParkName(parkCode)}${parkHoursDisplay ? ` (${parkHoursDisplay})` : ''}`
    sections.push(new Paragraph({
      text: parkHeadingText,
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 },
    }))

    // Park data — rides, shows, restaurants, arrival tips, LL tips
    const parkData = PARK_RIDES[parkCode] || UNIVERSAL_PARKS[parkCode]

    // Early Entry note for Disney resort guests
    const isDPark = ['mk', 'hs', 'ep', 'ak'].includes(parkCode)
    if (isDPark && hotelInfo.isDisneyGuest && dayHours.earlyEntry) {
      // Bold arrival time recommendation
      const earlyEntryTime = (typeof dayHours.earlyEntry === 'string' && dayHours.earlyEntry !== 'true') ? dayHours.earlyEntry : '7:00 am'
      sections.push(new Paragraph({
        children: [new TextRun({
          text: `Estén en el parque a las ${earlyEntryTime} para aprovechar el Early Entry.`,
          bold: true, color: '1565C0',
        })],
        spacing: { after: 60 },
      }))
      // Early Entry explanation (normal text)
      sections.push(new Paragraph({
        children: [new TextRun({
          text: `Ustedes tienen Early Entry por hospedarse en ${hotelName}. Entran 30 minutos antes que el público general, es la mejor ventaja para hacer las atracciones populares con menos fila.`,
        })],
        spacing: { after: 100 },
      }))

      // Early Entry gate location
      const earlyEntryGate = parkData?.earlyEntryGate
      if (earlyEntryGate) {
        sections.push(new Paragraph({
          children: [new TextRun({ text: `Por ser huéspedes, el ingreso es desde ${earlyEntryGate}.`, italics: true })],
          spacing: { after: 100 },
        }))
      }
    }

    // Skyliner entry tip for EPCOT
    if (parkCode === 'ep' && hotelInfo.isDisneyGuest) {
      const skylinerHotelName = (hotelInfo.hotelName || '').toLowerCase()
      const isSkyliner = SKYLINER_HOTELS.some(h => skylinerHotelName.includes(h.toLowerCase()))
      if (isSkyliner) {
        sections.push(new Paragraph({
          children: [new TextRun({ text: SKYLINER_EPCOT_TIP, italics: true })],
          spacing: { after: 200 },
        }))
      }
    }

    // Extended Evening Hours note for Deluxe Disney resort guests
    if (isDPark && hotelInfo.disneyTier === 'deluxe' && dayHours.extendedHours) {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: `✨ Por hospedarse en ${hotelName} (Deluxe), tienen Extended Evening Hours hasta las ${dayHours.extendedHours}. Menos gente, atracciones populares con filas cortísimas. Aprovechen para repetir las que más les gusten.`,
          bold: true, color: '6A1B9A',
        })],
        spacing: { after: 100 },
      }))
    }

    // Off-site note (no Disney hotel, visiting Disney park)
    if (isDPark && !hotelInfo.isDisneyGuest) {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: '💡 No se hospedan en hotel Disney, así que entran con el horario regular. Lleguen lo más temprano posible — los primeros 30 minutos del parque son los más valiosos.',
          italics: true,
        })],
        spacing: { after: 100 },
      }))
    }

    // Universal Express Pass note for Premier hotel guests
    const isUPark = ['uso', 'ioa', 'epic'].includes(parkCode)
    if (isUPark && hotelInfo.universalTier === 'premier') {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: `[EXPRESS] Por hospedarse en ${hotelName} (Premier), tienen Express Pass INCLUIDO para todo el grupo. No necesitan comprarlo aparte. Busquen la entrada "Universal Express" en cada atracción.`,
          bold: true, color: '2E7D32',
        })],
        spacing: { after: 100 },
      }))
    } else if (isUPark && hotelInfo.isUniversalGuest) {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: `[HOTEL] Por hospedarse en hotel Universal, tienen Early Park Admission — entran 1 hora antes a uno de los parques. Aprovéchenlo para hacer las atracciones más populares primero.`,
          bold: true, color: '1565C0',
        })],
        spacing: { after: 100 },
      }))
    }

    // Pace note (from guide form ritmo)
    if (gr.ritmo === 'relajado') {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: '💡 Hoy vamos con calma — no hay que correr. Disfruten cada momento sin prisa.',
          italics: true,
        })],
        spacing: { after: 150 },
      }))
    }

    // Per-park LL tip (short recommendation, full LL tables are in Datos importantes)
    const llChoice = gr[`ll_${parkCode}`]
    const llTipText = (noCoasters && parkData?.noCoasterLLTip) ? parkData.noCoasterLLTip : parkData?.llTip
    if (llTipText && llChoice !== 'no') {
      sections.push(new Paragraph({
        children: [new TextRun({ text: llTipText, bold: true })],
        spacing: { after: 150 },
      }))
    }

    // Arrival tip
    if (parkData?.arrivalTip) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: parkData.arrivalTip, italics: true })],
        spacing: { after: 200 },
      }))
    }

    // Walking directions to first ride
    if (parkData?.walkingDirections) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: parkData.walkingDirections, italics: true })],
        spacing: { after: 150 },
      }))
    }

    // Express Pass note (Universal parks)
    const epChoice = gr[`expressPass_${parkCode}`]
    if (['uso', 'ioa', 'epic'].includes(parkCode) && epChoice === 'yes') {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: '[EXPRESS] Tienen Express Pass — úsenlo en las filas más largas. Busquen la entrada "Universal Express" en cada atracción.',
          bold: true,
        })],
        spacing: { before: 150, after: 200 },
      }))
    }

    // LL turn order recommendation (before ride order)
    const parkLLTurns = (config.llTurnTimes || []).filter(t => t.parkCode === parkCode)
    if (parkLLTurns.length) {
      // Use Sara's specific turn times (#38)
      sections.push(new Paragraph({
        children: [new TextRun({ text: 'Orden ideal para toma de turnos de LIGHTING LANE:', bold: true })],
        spacing: { before: 200, after: 100 },
      }))
      for (const turn of parkLLTurns) {
        sections.push(new Paragraph({
          children: [
            new TextRun({ text: `[ ] ${turn.ride}` }),
            new TextRun({ text: turn.time ? ` (tipo ${turn.time})` : '', italics: true, color: '888888' }),
          ],
          spacing: { after: 30 },
        }))
      }
      sections.push(new Paragraph({ text: '', spacing: { after: 150 } }))
    } else if (parkData?.llGroups && isDPark) {
      const ll = parkData.llGroups
      sections.push(new Paragraph({
        children: [new TextRun({ text: 'Orden ideal para toma de turnos de LIGHTING LANE:', bold: true })],
        spacing: { before: 200, after: 100 },
      }))

      // Single Pass picks first (the paid individual ones)
      if (ll.singlePass?.length) {
        for (const sp of ll.singlePass) {
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: `[ ] ${sp.name}` }),
              new TextRun({ text: ' (LL Single Pass)', italics: true, color: '888888' }),
            ],
            spacing: { after: 30 },
          }))
        }
      }

      // Multi Pass Group 1 picks (these are the most popular, recommend taking first)
      if (ll.group1?.rides?.length) {
        for (const ride of ll.group1.rides.slice(0, 3)) {
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: `[ ] ${ride}` }),
              new TextRun({ text: ' (LL Multi Pass)', italics: true, color: '888888' }),
            ],
            spacing: { after: 30 },
          }))
        }
      }

      sections.push(new Paragraph({ text: '', spacing: { after: 150 } }))
    }

    // Ride order — filtered by guide form selections
    const parkClosures = closures[parkCode] || []
    const selectedRides = gr[`atracciones_${parkCode}`] || []
    if (parkData?.rideOrder) {
      sections.push(marker(SECTION_MARKERS.parkRides(parkCode)))
      const { primary, secondary, skipped, ifTimePermitsFiltered } = filterRides(parkData.rideOrder, selectedRides, hasGuideData, gr.ritmo, parkData.ifTimePermits || [])

      const mainRides = primary

      sections.push(...buildRideOrder(parkCode, mainRides, viajeros, parkClosures, { Paragraph, TextRun, HeadingLevel }, { noCoasters, endOfDayTip: parkData?.endOfDayTip }))

      // "If time permits" — combines filtered extras + overflow rides
      const ifTimeList = [...(ifTimePermitsFiltered || []), ...secondary]
      if (ifTimeList.length) {
        sections.push(new Paragraph({
          text: 'Si les queda tiempo:',
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
        }))
        for (const ride of ifTimeList) {
          if (isClosedAttraction(ride.name, parkClosures)) continue
          if (noCoasters && ride.isCoaster && ride.coasterType !== 'suave') continue
          {
            const childNotes = ride.key ? generateChildNotes(ride.key, viajeros) : null
            const iftParts = [new TextRun({ text: `[ ] ${ride.name}` })]
            if (ride.sara) iftParts.push(new TextRun({ text: ` — ${ride.sara}`, italics: true }))
            sections.push(new Paragraph({
              children: iftParts,
              spacing: { after: childNotes ? 20 : 40 },
            }))
            if (childNotes) {
              for (const note of childNotes) {
                sections.push(new Paragraph({
                  children: [new TextRun({ text: `     ${note}`, color: 'CC0000', size: 20 })],
                  spacing: { after: 20 },
                }))
              }
            }
          }
        }
      }

      // Not recommended — only if no guide data (Sara curates when she has form info)
      if (!hasGuideData && parkData.notRecommended?.length) {
        sections.push(new Paragraph({
          text: 'No tan recomendados:',
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
        }))
        for (const ride of parkData.notRecommended) {
          const nrParts = [new TextRun({ text: `${ride.name}` })]
          if (ride.sara) nrParts.push(new TextRun({ text: ` — ${ride.sara}`, italics: true }))
          sections.push(new Paragraph({ children: nrParts, spacing: { after: 40 } }))
        }
      }
    }

    // Universal top rides — filtered by guide form selections
    if (parkData?.topRides) {
      sections.push(marker(SECTION_MARKERS.parkRides(parkCode)))
      const { primary: uniPrimary, secondary: uniSecondary } = filterRides(parkData.topRides, selectedRides, hasGuideData, gr.ritmo, [])

      const uniMain = uniPrimary

      sections.push(new Paragraph({
        text: 'ORDEN IDEAL DEL PARQUE',
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 150 },
      }))
      for (const ride of uniMain) {
        if (isClosedAttraction(ride.name, parkClosures)) continue
        if (noCoasters && ride.isCoaster) {
          if (ride.coasterType === 'suave') { /* fall through */ }
          else if (ride.noCoasterNote) {
            const uniParts = [new TextRun({ text: `[ ] ${ride.name}` })]
            sections.push(new Paragraph({ children: uniParts, spacing: { after: 20 } }))
            sections.push(new Paragraph({
              children: [
                new TextRun({ text: '     \u21B3 Opcional: ', color: '888888', italics: true, size: 20 }),
                new TextRun({ text: ride.noCoasterNote, color: '888888', italics: true, size: 20 }),
              ],
              spacing: { after: 60 },
            }))
            continue
          } else { continue }
        }
        {
          const childNotes = ride.key ? generateChildNotes(ride.key, viajeros) : null
          const uniParts = [new TextRun({ text: `[ ] ${ride.name}` })]
          if (ride.sara) uniParts.push(new TextRun({ text: ` — ${ride.sara}`, italics: true }))
          sections.push(new Paragraph({
            children: uniParts,
            spacing: { after: childNotes ? 20 : 60 },
          }))
          if (childNotes) {
            for (const note of childNotes) {
              sections.push(new Paragraph({
                children: [new TextRun({ text: `    ${note}`, color: 'CC0000', size: 20 })],
                spacing: { after: 20 },
              }))
            }
          }
        }
      }

      const uniSecList = uniSecondary
      if (uniSecList.length) {
        sections.push(new Paragraph({
          text: 'Si les queda tiempo:',
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
        }))
        for (const ride of uniSecList) {
          if (isClosedAttraction(ride.name, parkClosures)) continue
          if (noCoasters && ride.isCoaster && ride.coasterType !== 'suave') continue
          {
            const childNotes = ride.key ? generateChildNotes(ride.key, viajeros) : null
            const uniSecParts = [new TextRun({ text: `[ ] ${ride.name}` })]
            if (ride.sara) uniSecParts.push(new TextRun({ text: ` — ${ride.sara}`, italics: true }))
            sections.push(new Paragraph({
              children: uniSecParts,
              spacing: { after: childNotes ? 20 : 40 },
            }))
            if (childNotes) {
              for (const note of childNotes) {
                sections.push(new Paragraph({
                  children: [new TextRun({ text: `     ${note}`, color: 'CC0000', size: 20 })],
                  spacing: { after: 20 },
                }))
              }
            }
          }
        }
      }

      // Diagon Alley exploration tip (USO)
      if (parkData.diagonAlleyTip) {
        sections.push(new Paragraph({ text: parkData.diagonAlleyTip, spacing: { after: 200 } }))
      }

      // Hogsmeade exploration tip (IOA)
      if (parkData.hogsmeadeTip) {
        sections.push(new Paragraph({ text: parkData.hogsmeadeTip, spacing: { after: 200 } }))
      }
    }

    // Epic Universe zones
    if (parkCode === 'epic') {
      sections.push(marker(SECTION_MARKERS.epicUniverse))
      sections.push(...buildEpicUniverse({ Paragraph, TextRun, HeadingLevel }))
    }

    // Restaurants — filtered by food preferences and dining plan type
    const restaurants = PARK_RESTAURANTS[parkCode]
    if (restaurants) {
      sections.push(new Paragraph({
        text: 'ALMUERZO O COMIDA',
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 250, after: 60 },
      }))
      // IMPORTANTE mobile order tip BEFORE the restaurant table
      sections.push(new Paragraph({
        children: [
          new TextRun({ text: 'IMPORTANTE: ', bold: true }),
          new TextRun({ text: 'Recuerden que pueden pedir desde el celular con Mobile Order y recoger en el restaurante sin hacer fila.' }),
        ],
        spacing: { after: 100 },
      }))
      sections.push(marker(SECTION_MARKERS.parkRestaurants(parkCode)))
      sections.push(...buildRestaurantTable(parkCode, restaurants, gr, diningPlanType, { Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType, TableLayoutType }))
    }

    // Dining reservations for this park+date (#37)
    const dayReservations = (config.diningReservations || []).filter(r =>
      r.parkCode === parkCode && (!r.date || r.date === day.date)
    )
    if (dayReservations.length) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: 'RESERVACIONES DEL DÍA:', bold: true })],
        spacing: { before: 150, after: 60 },
      }))
      for (const res of dayReservations) {
        sections.push(new Paragraph({
          children: [
            new TextRun({ text: `• ${res.restaurant}`, bold: true }),
            new TextRun({ text: res.time ? ` a las ${res.time}` : '' }),
          ],
          spacing: { after: 30 },
        }))
      }
    }

    // Entertainment data — prefer date-specific from Disney Finder API, fallback to flat/live/static
    // config.entertainment can be: {"2026-03-05": {"mk": [...]}} (per-date) or {"mk": [...]} (flat/live)
    const dateEntertainment = config?.entertainment?.[day.date]?.[parkCode]  // per-date format
    const flatEntertainment = config?.entertainment?.[parkCode]  // flat format (legacy/live)
    const entertainmentLive = config?.entertainmentLive?.[parkCode]  // ThemeParks.wiki live fallback
    const parkEntertainment = dateEntertainment || flatEntertainment || entertainmentLive || PARK_ENTERTAINMENT[parkCode]

    // Characters — use live hours from backend when available
    const characters = PARK_CHARACTERS[parkCode]
    if (characters) {
      // Build a map of live character hours from entertainment data (date-specific or flat)
      const charEntSource = dateEntertainment || flatEntertainment || entertainmentLive || []
      const liveCharEntries = charEntSource.filter(e => e.type === 'character')
      const liveCharMap = {}
      for (const lc of liveCharEntries) {
        liveCharMap[lc.name.toLowerCase()] = lc.hours || ''
      }

      sections.push(marker(SECTION_MARKERS.parkCharacters(parkCode)))
      sections.push(new Paragraph({
        children: [new TextRun({ text: 'PERSONAJES POR CONOCER:', bold: true })],
        spacing: { before: 200, after: 100 },
      }))
      for (const char of characters) {
        // Try fuzzy match for live hours: check if any live entry name includes static name or vice versa
        let hours = char.hours || ''
        const charNameLower = char.name.toLowerCase()
        for (const [liveName, liveHours] of Object.entries(liveCharMap)) {
          if (liveHours && (liveName.includes(charNameLower) || charNameLower.includes(liveName))) {
            hours = liveHours
            break
          }
        }

        const locationText = hours
          ? ` en ${char.location} ${hours} (revisar filas)`
          : ` en ${char.location} (revisar filas)`
        sections.push(new Paragraph({
          children: [
            new TextRun({ text: '[ ] ' }),
            new TextRun({ text: char.name, bold: true }),
            new TextRun({ text: locationText }),
          ],
          spacing: { after: 40 },
        }))
      }
    }

    // AK river parade note (after characters)
    if (parkCode === 'ak') {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: '*Por el río pasa más o menos cada hora un bote con un mini parade con algunos personajes, estén pendientes.',
          italics: true,
        })],
        spacing: { before: 60, after: 100 },
      }))
    }

    // Entertainment schedule table
    const entertainmentConfig = { ...config, _dateEntertainment: dateEntertainment || flatEntertainment || entertainmentLive }
    if (parkEntertainment?.length) {
      sections.push(...buildEntertainmentTable(parkCode, entertainmentConfig, { Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, ShadingType, AlignmentType, TableLayoutType }))
    }

    // Shows — filtered by guide form selections
    const allShows = PARK_SHOWS[parkCode]
    if (allShows) {
      const selectedShows = gr[`shows_${parkCode}`] || []
      const filteredShows = filterShows(allShows, selectedShows, hasGuideData, gr.finalDia)

      if (filteredShows.length) {
        sections.push(marker(SECTION_MARKERS.parkShows(parkCode)))
        sections.push(new Paragraph({
          text: 'Shows y Espectáculos',
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
        }))
        for (const show of filteredShows) {
          const showClosed = isClosedAttraction(show.name, parkClosures)
          if (showClosed) continue
          {
            const showParts = [
              new TextRun({ text: `[ ] ${show.name}`, bold: true }),
              new TextRun({ text: ` (${show.times})` }),
            ]
            if (show.sara) showParts.push(new TextRun({ text: ` — ${show.sara}`, italics: true }))
            sections.push(new Paragraph({ children: showParts, spacing: { after: 40 } }))
          }
        }
      }
    }

    // End-of-day note
    if (gr.finalDia === 'hotel') {
      sections.push(new Paragraph({
        children: [new TextRun({
          text: 'Después de cenar, de vuelta al hotel a descansar. Mañana es otro día mágico.',
          italics: true,
        })],
        spacing: { before: 150, after: 100 },
      }))
    }

    // Evening plans for this date (#39)
    const dayEveningPlan = (config.eveningPlans || []).find(p => p.date === day.date)
    if (dayEveningPlan) {
      sections.push(new Paragraph({
        children: [
          new TextRun({ text: 'Plan de la noche: ', bold: true }),
          new TextRun({ text: dayEveningPlan.plan }),
        ],
        spacing: { before: 100, after: 150 },
      }))
    }
  }

  // ── HOTEL TRANSITION DAYS (multi-hotel trips) ──
  if (lead.hotels?.length > 1) {
    for (let h = 0; h < lead.hotels.length - 1; h++) {
      const fromHotel = lead.hotels[h]
      const toHotel = lead.hotels[h + 1]
      if (fromHotel.checkOut || toHotel.checkIn) {
        const transDate = fromHotel.checkOut || toHotel.checkIn
        sections.push(new Paragraph({
          text: `${formatDateLabel(transDate)} — Cambio de Hotel`,
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
        }))
        sections.push(new Paragraph({
          children: [
            new TextRun({ text: `Check-out ${fromHotel.name || 'hotel anterior'}`, bold: true }),
            new TextRun({ text: ` → ` }),
            new TextRun({ text: `Check-in ${toHotel.name || 'nuevo hotel'}`, bold: true }),
          ],
          spacing: { after: 100 },
        }))
        for (const line of HOTEL_TRANSITION.template.split('\n')) {
          const trimmed = line.trim()
          if (!trimmed) continue
          sections.push(new Paragraph({ text: trimmed, spacing: { after: 60 } }))
        }
      }
    }
  }

  // ── CHECKOUT DAY ──
  sections.push(...buildCheckoutDay(lead, hotelInfo, { Paragraph, TextRun, HeadingLevel }))

  // ── CITY WALK (Universal only) ──
  if (isUniversal) {
    sections.push(marker(SECTION_MARKERS.cityWalk))
    sections.push(...buildCityWalk(gr, { Paragraph, TextRun, HeadingLevel }))
  }

  // ── SINGLE RIDER (Universal only) ──
  if (isUniversal) {
    const SR = SINGLE_RIDER
    sections.push(new Paragraph({
      children: [new TextRun({ text: SR.title, bold: true, size: 24 })],
      spacing: { before: 400, after: 100 },
    }))
    sections.push(new Paragraph({ text: SR.explanation, spacing: { after: 120 } }))
    sections.push(new Paragraph({
      children: [new TextRun({ text: 'Atracciones que ofrecen Single Rider:', bold: true })],
      spacing: { after: 60 },
    }))
    for (const ride of SR.rides) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: `• ${ride}` })],
        spacing: { after: 20 },
      }))
    }
  }

  // ── MEAL PLAN ──
  if (hasDining) {
    sections.push(marker(SECTION_MARKERS.mealPlan))
    sections.push(...buildMealPlan(lead, diningPlanType, { Paragraph, TextRun, HeadingLevel }))
  }

  // ── SPECIAL EXPERIENCES (from guide form) ──
  if (gr.experiencias?.length) {
    sections.push(...buildSpecialExperiences(gr, parks, { Paragraph, TextRun, HeadingLevel }))
  }

  // Separator before INFO_ADICIONAL
  sections.push(new Paragraph({
    children: [new TextRun({ text: '────────────────────────────────────────────────────────────────', color: '999999' })],
    spacing: { before: 400, after: 200 },
  }))
  // ── INFORMACIÓN ADICIONAL ──
  sections.push(marker(SECTION_MARKERS.infoAdicional))
  sections.push(...buildInfoAdicional({ Paragraph, TextRun, HeadingLevel }))

  // ── SHOPPING ──
  sections.push(marker(SECTION_MARKERS.shopping))
  sections.push(...buildShopping({ Paragraph, TextRun, HeadingLevel }))

  // ── PERSONALIZATION SUMMARY (for Sara) ──
  if (hasGuideData) {
    sections.push(marker(SECTION_MARKERS.personalization))
    sections.push(...buildPersonalizationSummary(lead, gr, parks, diningPlanType, viajeros, { Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, ShadingType, AlignmentType }))
  }

  // Build document
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: { top: 720, bottom: 720, left: 720, right: 720 },
        },
      },
      children: sections,
    }],
  })

  // Generate blob
  const blob = await Packer.toBlob(doc)

  // Clean up section texts (trim whitespace, remove empty)
  const cleanSections = {}
  for (const [key, text] of Object.entries(_sectionTexts)) {
    const cleaned = text.replace(/\s+/g, ' ').trim()
    if (cleaned) cleanSections[key] = cleaned
  }

  return {
    blob,
    filename: `Itinerario - Familia ${familyName}.docx`,
    sections: cleanSections,
  }
}

// ── SECTION BUILDERS ─────────────────────────────────────────────────────

function buildGreeting(familyName, isDisney, isUniversal, D) {
  let heading = GREETING.heading.replace('{familyName}', familyName)
  if (isDisney && isUniversal) heading = GREETING.headingCombo.replace('{familyName}', familyName)
  else if (isUniversal && !isDisney) heading = GREETING.headingUniversal.replace('{familyName}', familyName)

  return [
    new D.Paragraph({
      text: heading,
      heading: D.HeadingLevel.HEADING_1,
      alignment: D.AlignmentType.CENTER,
      spacing: { after: 300 },
    }),
    new D.Paragraph({
      text: GREETING.welcome,
      spacing: { after: 300 },
    }),
  ]
}

function buildAgenda(lead, parks, config, D) {
  const result = [
    new D.Paragraph({
      text: 'AGENDA DEL VIAJE',
      heading: D.HeadingLevel.HEADING_2,
      spacing: { before: 200, after: 200 },
    }),
  ]

  const schedule = config.parkSchedule || buildDefaultSchedule(lead, parks)
  if (!schedule.length) {
    result.push(new D.Paragraph({
      text: '[Sara: completa la agenda con las fechas y parques del viaje]',
      spacing: { after: 200 },
    }))
    return result
  }

  // Simple table: Date (20%) | Day (15%) | Activity (65%)
  const agendaCols = [Math.round(TABLE_DXA * 0.20), Math.round(TABLE_DXA * 0.15), Math.round(TABLE_DXA * 0.65)]
  const rows = [
    new D.TableRow({
      children: [
        tableCell('Fecha', D, true, 20),
        tableCell('Día', D, true, 15),
        tableCell('Actividad', D, true, 65),
      ],
    }),
  ]

  const hotel = getFirstHotel(lead)

  // Helper: format date string to "Lun 2 Mar"
  function fmtDate(dateStr) {
    if (!dateStr) return ''
    try {
      const d = new Date(dateStr + 'T12:00:00')
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      return `${dayNames[d.getDay()]} ${d.getDate()} ${monthNames[d.getMonth()]}`
    } catch { return formatDateShort(dateStr) }
  }

  // Helper: add N days to an ISO date string
  function addDays(dateStr, n) {
    const d = new Date(dateStr + 'T12:00:00')
    d.setDate(d.getDate() + n)
    return d.toISOString().split('T')[0]
  }

  // Check-in date — config override > hotel check-in > travel date > first park date - 1
  const firstParkDate = schedule[0]?.date || ''
  const checkInStr = config.checkInDate
    || lead.hotels?.[0]?.checkIn
    || lead.travelDate
    || (firstParkDate ? addDays(firstParkDate, -1) : '')
  const hasCheckIn = !!checkInStr

  let dayNum = 1

  if (hasCheckIn) {
    rows.push(new D.TableRow({
      children: [
        tableCell(fmtDate(checkInStr), D, false, 20),
        tableCell(`Día ${dayNum}`, D, false, 15),
        tableCell(`Llegada + Check-in ${hotel} — Explorar hotel, piscina, Disney Springs`, D, false, 65),
      ],
    }))
    dayNum++
  }

  // Build park day rows WITH rest days for gaps between park dates
  for (let i = 0; i < schedule.length; i++) {
    const day = schedule[i]
    const prevDate = i === 0 ? checkInStr : schedule[i - 1]?.date

    // Detect gap days between previous date and this park day → insert rest days
    if (prevDate && day.date) {
      let gapDate = addDays(prevDate, 1)
      while (gapDate < day.date) {
        rows.push(new D.TableRow({
          children: [
            tableCell(fmtDate(gapDate), D, false, 20),
            tableCell(`Día ${dayNum}`, D, false, 15),
            tableCell(`Día libre — Descanso en ${hotel}, piscina, compras`, D, false, 65),
          ],
        }))
        dayNum++
        gapDate = addDays(gapDate, 1)
      }
    }

    rows.push(new D.TableRow({
      children: [
        tableCell(day.dateLabel || fmtDate(day.date), D, false, 20),
        tableCell(`Día ${dayNum}`, D, false, 15),
        tableCell(`${getParkName(day.parkCode)} *`, D, false, 65),
      ],
    }))
    dayNum++
  }

  // Check-out row — config override > hotel check-out > last park date + 1
  const lastParkDate = schedule[schedule.length - 1]?.date || ''
  const checkOutStr = config.checkOutDate
    || lead.hotels?.[0]?.checkOut
    || (lastParkDate ? addDays(lastParkDate, 1) : '')

  if (checkOutStr) {
    // Insert rest days between last park day and checkout if there's a gap
    if (lastParkDate) {
      let gapDate = addDays(lastParkDate, 1)
      while (gapDate < checkOutStr) {
        rows.push(new D.TableRow({
          children: [
            tableCell(fmtDate(gapDate), D, false, 20),
            tableCell(`Día ${dayNum}`, D, false, 15),
            tableCell(`Día libre — Descanso en ${hotel}, piscina, compras`, D, false, 65),
          ],
        }))
        dayNum++
        gapDate = addDays(gapDate, 1)
      }
    }

    rows.push(new D.TableRow({
      children: [
        tableCell(fmtDate(checkOutStr), D, false, 20),
        tableCell(`Día ${dayNum}`, D, false, 15),
        tableCell(`Check-out ${hotel} — Compras / Disney Springs / Regreso`, D, false, 65),
      ],
    }))
  }

  result.push(new D.Table({
    columnWidths: agendaCols,
    rows,
    width: { size: TABLE_DXA, type: D.WidthType.DXA },
    layout: D.TableLayoutType.FIXED,
  }))

  return result
}

function buildReservationCodes(lead, D) {
  const codes = []

  // Hotel reservation codes
  if (lead.hotels?.length) {
    for (const h of lead.hotels) {
      if (h.reservationNum) {
        codes.push({ label: `Código de reserva del hotel${h.name ? ' (' + h.name + ')' : ''}`, code: h.reservationNum })
      }
    }
  }

  // Park entry codes
  if (lead.parkEntries?.length) {
    for (const pe of lead.parkEntries) {
      if (pe.reservationNum) {
        const parkLabel = pe.park || 'Parque'
        codes.push({ label: `Código de ${parkLabel}`, code: pe.reservationNum })
      }
    }
  }

  // Cruise codes
  if (lead.cruises?.length) {
    for (const cr of lead.cruises) {
      if (cr.reservationNum) {
        codes.push({ label: `Código de reserva ${cr.line || 'crucero'}`, code: cr.reservationNum })
      }
    }
  }

  // Main reservation number
  if (lead.reservationNum && !codes.some(c => c.code === lead.reservationNum)) {
    codes.push({ label: 'Código de Parque', code: lead.reservationNum })
  }

  if (codes.length === 0) return []

  const result = []
  for (const c of codes) {
    result.push(new D.Paragraph({
      children: [
        new D.TextRun({ text: `${c.label}: `, bold: true }),
        new D.TextRun({ text: c.code }),
      ],
      spacing: { after: 40 },
    }))
  }
  result.push(new D.Paragraph({ text: '', spacing: { after: 200 } }))
  return result
}

function buildLightningLane(parks, hotelInfo, lead, D) {
  const result = []
  const LL = LIGHTNING_LANE
  const disneyParks = parks.filter(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  const noBorder = { style: D.BorderStyle.NONE, size: 0, color: 'FFFFFF' }
  const thinBorder = { style: D.BorderStyle.SINGLE, size: 1, color: '000000' }
  const allBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder }

  // ── Booking window: 7 days for Disney hotel guests, 3 days for ticket-only ──
  const bookingDays = hotelInfo.isDisneyGuest ? 7 : 3

  // Calculate specific booking dates from travel start
  let bookingDateStr = ''
  const travelStart = getTravelStartDate(lead)
  if (travelStart) {
    const bookingDate = new Date(travelStart)
    bookingDate.setDate(bookingDate.getDate() - bookingDays)
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
    bookingDateStr = `${bookingDate.getDate()} de ${months[bookingDate.getMonth()]} de ${bookingDate.getFullYear()}`
  }

  // ── SECTION HEADING ──
  result.push(new D.Paragraph({
    text: LL.title,
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 200 },
  }))

  // ── LL INTRO ("TODO SOBRE LAS FILAS RAPIDAS") ──
  if (LL.intro) {
    result.push(new D.Paragraph({ text: LL.intro, spacing: { after: 200 } }))
  }

  // ── MULTI PASS ──
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: LL.multipass.title, bold: true, size: 24 })],
    spacing: { after: 80 },
  }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: `• ${LL.multipass.priceRange}` })],
    spacing: { after: 40 },
  }))
  // Per-park prices (only parks in this itinerary)
  const parkCodeToKey = { mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom' }
  for (const pp of LL.multipass.perPark) {
    if (disneyParks.some(p => parkCodeToKey[p] === pp.park)) {
      result.push(new D.Paragraph({
        text: `${pp.park}: ${pp.price}`,
        spacing: { after: 20 },
      }))
    }
  }
  result.push(new D.Paragraph({ text: '', spacing: { after: 80 } }))

  // Hotel-aware booking window text
  const buyAdvanceText = `Tomar ${bookingDays} días antes los turnos para las atracciones que deseen con el Lighting Lane Multi Pass. En cada parque podrán reservar 3 rides o experiencias. Estas experiencias podrán reservarlas de la siguiente manera (Ver cuadro)`
  result.push(new D.Paragraph({ text: buyAdvanceText, spacing: { after: 120 } }))

  // ── LL GROUP TABLES — pair parks side by side like Sara's PDF ──
  // Build table data for each Disney park in this itinerary
  const parkTableData = []
  for (const pc of disneyParks) {
    const parkData = PARK_RIDES[pc]
    if (!parkData?.llGroups) continue
    const ll = parkData.llGroups
    parkTableData.push({ code: pc, name: parkData.name, groups: ll })
  }

  // Pair parks into 2-column tables (MK+AK, HS+EPCOT, or whatever combo)
  for (let t = 0; t < parkTableData.length; t += 2) {
    const left = parkTableData[t]
    const right = parkTableData[t + 1]
    const colWidth = right ? Math.round(TABLE_DXA * 0.50) : TABLE_DXA

    // Helper to build cell content (group labels + bullet rides)
    const buildGroupCell = (pd) => {
      const children = []
      if (pd.groups.group1) {
        children.push(new D.Paragraph({
          children: [new D.TextRun({ text: pd.groups.group1.label, bold: true })],
          spacing: { after: 40 },
        }))
        for (const ride of pd.groups.group1.rides) {
          children.push(new D.Paragraph({
            children: [new D.TextRun({ text: `•  ${ride}` })],
            spacing: { after: 20 },
          }))
        }
      }
      if (pd.groups.group2) {
        children.push(new D.Paragraph({
          children: [new D.TextRun({ text: pd.groups.group2.label, bold: true })],
          spacing: { before: 80, after: 40 },
        }))
        for (const ride of pd.groups.group2.rides) {
          children.push(new D.Paragraph({
            children: [new D.TextRun({ text: `•  ${ride}` })],
            spacing: { after: 20 },
          }))
        }
      }
      return children
    }

    // Header row
    const headerCells = [
      new D.TableCell({
        children: [new D.Paragraph({
          children: [new D.TextRun({ text: `En ${left.name}`, bold: true })],
          alignment: D.AlignmentType.CENTER,
        })],
        width: { size: colWidth, type: D.WidthType.DXA },
        borders: allBorders,
        shading: { type: D.ShadingType?.SOLID || 'solid', color: 'F0F0F0' },
      }),
    ]
    if (right) {
      headerCells.push(new D.TableCell({
        children: [new D.Paragraph({
          children: [new D.TextRun({ text: `En ${right.name}`, bold: true })],
          alignment: D.AlignmentType.CENTER,
        })],
        width: { size: colWidth, type: D.WidthType.DXA },
        borders: allBorders,
        shading: { type: D.ShadingType?.SOLID || 'solid', color: 'F0F0F0' },
      }))
    }

    // Content row
    const contentCells = [
      new D.TableCell({
        children: buildGroupCell(left),
        width: { size: colWidth, type: D.WidthType.DXA },
        borders: allBorders,
      }),
    ]
    if (right) {
      contentCells.push(new D.TableCell({
        children: buildGroupCell(right),
        width: { size: colWidth, type: D.WidthType.DXA },
        borders: allBorders,
      }))
    }

    const colWidths = right
      ? [Math.round(TABLE_DXA * 0.50), Math.round(TABLE_DXA * 0.50)]
      : [TABLE_DXA]
    result.push(new D.Table({
      columnWidths: colWidths,
      rows: [
        new D.TableRow({ children: headerCells }),
        new D.TableRow({ children: contentCells }),
      ],
      width: { size: TABLE_DXA, type: D.WidthType.DXA },
      layout: D.TableLayoutType.FIXED,
    }))
    result.push(new D.Paragraph({ text: '', spacing: { after: 150 } }))
  }

  // ── CREDIT CARD WARNING ──
  result.push(new D.Paragraph({
    children: [
      new D.TextRun({ text: `${LL.creditCardWarningPrefix} `, bold: true }),
      new D.TextRun({ text: LL.creditCardWarningText }),
    ],
    spacing: { after: 200 },
  }))

  // ── BOOKING WINDOW REMINDER ──
  const reminderText = hotelInfo.isDisneyGuest
    ? 'RECUERDEN QUE SOLO SE PUEDE COMPRARLA HASTA 7 DÍAS ANTES. APROVECHEN ESTE BENEFICIO DE SER HUESPEDES DE DISNEY.'
    : 'RECUERDEN QUE SOLO SE PUEDE COMPRARLA HASTA 3 DÍAS ANTES.'
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: reminderText, bold: true })],
    spacing: { after: 80 },
  }))

  // ── SINGLE PASS ──
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: LL.singlePass.title, bold: true })],
    spacing: { after: 40 },
  }))
  const singlePassAdvance = `Se puede comprar hasta ${bookingDays} DÍAS ANTES DEL PARQUE.`
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: singlePassAdvance, bold: true })],
    spacing: { after: 40 },
  }))
  result.push(new D.Paragraph({ text: 'Ten en cuenta:', spacing: { after: 40 } }))
  for (const rule of LL.singlePass.rules) {
    result.push(new D.Paragraph({ text: rule, spacing: { after: 20 } }))
  }
  result.push(new D.Paragraph({ text: '', spacing: { after: 80 } }))

  // Single Pass per-park prices
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'Listado de filas individuales pagas en cada parque:', bold: true })],
    spacing: { after: 60 },
  }))
  for (const sp of LL.singlePass.perPark) {
    // Only show parks in this itinerary
    const parkMatch = disneyParks.some(p => sp.park.toLowerCase().includes(parkCodeToKey[p]?.toLowerCase()))
    if (parkMatch || disneyParks.length === 4) { // show all if all 4 parks
      result.push(new D.Paragraph({
        children: [
          new D.TextRun({ text: `${sp.park}: `, underline: {} }),
          new D.TextRun({ text: sp.items }),
        ],
        spacing: { after: 30 },
      }))
    }
  }
  result.push(new D.Paragraph({ text: '', spacing: { after: 150 } }))

  // ── MULTI PASS EXPLANATION ──
  result.push(new D.Paragraph({
    children: [
      new D.TextRun({ text: 'Recuerden que tendrán nuevas oportunidades para tomar un turno de ' }),
      new D.TextRun({ text: 'Lighting Lane Multi Pass', bold: true, underline: {} }),
      new D.TextRun({ text: ' (fila rápida, aparece en el app como LL) en cuanto hayan soltado uno de los turnos previamente tomados. Solo se podrá tomar ' }),
      new D.TextRun({ text: 'UN (1) turno al día por atracción', bold: true }),
      new D.TextRun({ text: ', es decir que no podrás volver a programar turno para una atracción que ya disfrutaste.' }),
    ],
    spacing: { after: 150 },
  }))

  // ── TIP BOARD ──
  result.push(new D.Paragraph({
    children: [
      new D.TextRun({ text: 'En la parte superior de la aplicación de Disney en ' }),
      new D.TextRun({ text: 'My Day Tip Board', bold: true, underline: {} }),
      new D.TextRun({ text: ' aparecen todas las atracciones y bajo el símbolo ' }),
      new D.TextRun({ text: 'LL MultiPass Experience', italics: true }),
      new D.TextRun({ text: ' encontrarán el horario disponible más temprano para tomar turno en las diferentes atracciones que tienen disponibilidad. Recuerden que en esta parte aparecen tanto tiempos de espera normales ' }),
      new D.TextRun({ text: '(Standby Lane)', bold: true }),
      new D.TextRun({ text: ' y Horarios de turno (LL) ' }),
      new D.TextRun({ text: 'MultiPass Experience', italics: true }),
      new D.TextRun({ text: ' o ' }),
      new D.TextRun({ text: 'LL SinglePass Experience', italics: true }),
      new D.TextRun({ text: ' (en el caso de las filas por pago individual).' }),
    ],
    spacing: { after: 200 },
  }))

  // ── CAST MEMBERS + CANCELLATION ──
  for (const line of LL.castMembers.split('\n')) {
    if (line.trim()) {
      result.push(new D.Paragraph({ text: line.trim(), spacing: { after: 40 } }))
    }
  }
  result.push(new D.Paragraph({ text: '', spacing: { after: 100 } }))
  result.push(new D.Paragraph({ text: LL.cancellation, spacing: { after: 200 } }))

  // ── MY DISNEY PLAN HEADING ──
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'MY DISNEY PLAN: Listos para vivir una experiencia mágica', bold: true, underline: {}, color: '1565C0' })],
    alignment: D.AlignmentType.CENTER,
    spacing: { before: 200, after: 200 },
  }))

  // ── COMODÍN ──
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: LL.comodin, italics: true })],
    spacing: { after: 300 },
  }))

  return result
}

function buildDisneySprings(gr, D) {
  const result = []
  result.push(new D.Paragraph({
    text: DISNEY_SPRINGS.title,
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 100 },
  }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: DISNEY_SPRINGS.intro, italics: true })],
    spacing: { after: 200 },
  }))

  for (const cat of DISNEY_SPRINGS.categories) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: cat.name, bold: true })],
      spacing: { before: 150, after: 80 },
    }))
    for (const r of cat.restaurants) {
      result.push(new D.Paragraph({
        children: [
          new D.TextRun({ text: `${r.name}: `, bold: true }),
          new D.TextRun({ text: r.tip }),
        ],
        spacing: { after: 40 },
      }))
    }
  }
  return result
}

function buildCheckInDay(lead, hotelInfo, config, D) {
  const hotel = hotelInfo.hotelName || getFirstHotel(lead)
  const text = CHECK_IN_DAY.template
    .replace('{hotelName}', hotel)

  const result = []

  // Date-specific heading (matches park day format)
  const checkInDate = lead.hotels?.[0]?.checkIn || lead.travelDate || ''
  const checkInLabel = checkInDate ? formatDateLabel(checkInDate) : ''
  const headingText = checkInLabel ? `${checkInLabel}: ${CHECK_IN_DAY.title}` : CHECK_IN_DAY.title

  result.push(new D.Paragraph({
    text: headingText,
    heading: D.HeadingLevel.HEADING_1,
    spacing: { before: 400, after: 200 },
  }))

  // Split by newlines and create paragraphs
  for (const line of text.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed) continue
    result.push(new D.Paragraph({
      text: trimmed,
      spacing: { after: 60 },
    }))
  }

  // Hotel benefits summary
  const benefits = []
  if (hotelInfo.isDisneyGuest) {
    benefits.push('Early Entry (30 min antes de la apertura) en TODOS los parques Disney')
    if (hotelInfo.disneyTier === 'deluxe') {
      benefits.push('Extended Evening Hours en parques selectos (revisen la app para horarios)')
    }
    benefits.push('Transporte gratis Disney (buses, monoriel, Skyliner, boats)')
    benefits.push('MagicBand+ funcional desde el check-in')
  }
  if (hotelInfo.isUniversalGuest) {
    if (hotelInfo.universalTier === 'premier') {
      benefits.push('Express Pass INCLUIDO para todo el grupo')
    }
    benefits.push('Early Park Admission (1 hora antes) en un parque Universal')
    benefits.push('Transporte gratis a los parques Universal')
  }

  if (benefits.length) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: `\nBeneficios de su hotel (${hotel}):`, bold: true })],
      spacing: { before: 200, after: 80 },
    }))
    for (const b of benefits) {
      result.push(new D.Paragraph({ text: b, spacing: { after: 40 } }))
    }
  }

  // Magical Extras note for Disney hotel guests
  if (hotelInfo.isDisneyGuest && CHECK_IN_DAY.magicalExtrasNote) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: CHECK_IN_DAY.magicalExtrasNote, italics: true })],
      spacing: { before: 150, after: 100 },
    }))
  }

  // Disney Springs reservations on check-in day
  const ciDate = lead.hotels?.[0]?.checkIn || lead.travelDate || ''
  const dsReservations = (config?.diningReservations || []).filter(r =>
    r.parkCode === 'ds' && (!r.date || r.date === ciDate)
  )
  if (dsReservations.length) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: 'RESERVACIONES DEL DÍA:', bold: true })],
      spacing: { before: 150, after: 60 },
    }))
    for (const res of dsReservations) {
      result.push(new D.Paragraph({
        children: [
          new D.TextRun({ text: `• ${res.restaurant}`, bold: true }),
          new D.TextRun({ text: res.time ? ` a las ${res.time}` : '' }),
        ],
        spacing: { after: 30 },
      }))
    }
  }

  return result
}

function buildCheckoutDay(lead, hotelInfo, D) {
  const hotel = hotelInfo.hotelName || getFirstHotel(lead)
  const result = []

  // Date-specific heading
  const checkOutDate = lead.hotels?.[0]?.checkOut || ''
  const checkOutLabel = checkOutDate ? formatDateLabel(checkOutDate) : ''
  const headingText = checkOutLabel
    ? `${checkOutLabel}: ${CHECKOUT_DAY.title}`
    : `${CHECKOUT_DAY.title} — ${hotel}`

  result.push(new D.Paragraph({
    text: headingText,
    heading: D.HeadingLevel.HEADING_1,
    spacing: { before: 400, after: 200 },
  }))

  for (const line of CHECKOUT_DAY.template.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed) continue
    result.push(new D.Paragraph({
      text: trimmed,
      spacing: { after: 60 },
    }))
  }

  return result
}

// buildLLGroups removed — LL groups now rendered as tables in buildLightningLane()

function buildRideOrder(parkCode, rideOrder, viajeros, parkClosures, D, { noCoasters = false, endOfDayTip = null } = {}) {
  const result = []
  result.push(new D.Paragraph({
    text: 'ORDEN IDEAL DEL PARQUE',
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 300, after: 150 },
  }))

  // Narrative connectors to insert AFTER each group finishes
  const narrativeMap = {
    mk: {
      1: [PARK_NARRATIVE.mk.afterOpeningRides],
      2: [PARK_NARRATIVE.mk.cavalcadeNote, PARK_NARRATIVE.mk.philharmagicIntro],
      3: [PARK_NARRATIVE.mk.lunchTransition, PARK_NARRATIVE.mk.paradeViewing],
      4: [PARK_NARRATIVE.mk.tomorrowlandTransition],
    },
    hs: {
      1: [PARK_NARRATIVE.hs.afterToyStoryLand],
      2: [PARK_NARRATIVE.hs.lunchTransition],
    },
    ep: {
      1: [PARK_NARRATIVE.ep.afterInitialRides, PARK_NARRATIVE.ep.afterFutureWorld],
      2: [PARK_NARRATIVE.ep.lunchTransition, PARK_NARRATIVE.ep.moanaAndWorldShowcase],
    },
    ak: {
      1: [PARK_NARRATIVE.ak.afterPandora],
      2: [PARK_NARRATIVE.ak.lunchTransition, PARK_NARRATIVE.ak.asiaTransition],
    },
  }

  const parkNarrative = narrativeMap[parkCode] || {}
  let currentGroup = null

  for (let i = 0; i < rideOrder.length; i++) {
    const ride = rideOrder[i]
    const isClosed = isClosedAttraction(ride.name, parkClosures)
    const childNotes = ride.key ? generateChildNotes(ride.key, viajeros) : null

    // Insert narrative connector when group changes
    if (ride.group && currentGroup !== null && ride.group !== currentGroup) {
      const connectors = parkNarrative[currentGroup]
      if (connectors) {
        for (const text of connectors) {
          if (text) {
            result.push(new D.Paragraph({
              children: [new D.TextRun({ text, italics: true, color: '555555' })],
              spacing: { before: 160, after: 160 },
            }))
          }
        }
      }
    }
    if (ride.group) currentGroup = ride.group

    if (isClosed) continue

    // No-coaster inline handling (Sara's approach)
    if (noCoasters && ride.isCoaster) {
      if (ride.coasterType === 'suave') {
        // Fall through to normal rendering below
      } else if (ride.noCoasterNote) {
        // Intensa with note: render ride name + optional note below
        const star = ride._selected ? '* ' : ''
        const parts = [new D.TextRun({ text: `[ ] ${star}${ride.name}`, bold: !!ride._selected })]
        result.push(new D.Paragraph({ children: parts, spacing: { after: 20 } }))
        result.push(new D.Paragraph({
          children: [
            new D.TextRun({ text: '     \u21B3 Opcional: ', color: '888888', italics: true, size: 20 }),
            new D.TextRun({ text: ride.noCoasterNote, color: '888888', italics: true, size: 20 }),
          ],
          spacing: { after: 60 },
        }))
        if (childNotes) {
          for (const note of childNotes) {
            result.push(new D.Paragraph({
              children: [new D.TextRun({ text: `     ${note}`, color: 'CC0000', size: 20 })],
              spacing: { after: 20 },
            }))
          }
        }
        if (ride.group) currentGroup = ride.group
        continue
      } else {
        // Intensa without note or extrema: silently skip
        continue
      }
    }

    // TRON adult-only positioning (#41) — note for parties with young children who can't ride
    if (ride.key === 'tron_lightcycle' && viajeros.some(v => v.esMenor && (AGE_TO_HEIGHT[v.edad] || 160) < 122)) {
      result.push(new D.Paragraph({
        children: [new D.TextRun({
          text: 'Si ustedes como adultos quieren hacer Tron, este es el mejor momento mientras los niños hacen otra atracción cercana.',
          italics: true, color: '1565C0',
        })],
        spacing: { before: 60, after: 30 },
      }))
    }

    {
      const star = ride._selected ? '*' : ''
      const parts = [new D.TextRun({ text: `[ ] ${star}${ride.name}`, bold: !!ride._selected })]
      if (ride.sara) parts.push(new D.TextRun({ text: ` — ${ride.sara}`, italics: true }))
      result.push(new D.Paragraph({
        children: parts,
        spacing: { after: childNotes ? 20 : 60 },
      }))

      if (childNotes) {
        for (const note of childNotes) {
          result.push(new D.Paragraph({
            children: [new D.TextRun({ text: `     ${note}`, color: 'CC0000', size: 20 })],
            spacing: { after: 20 },
          }))
        }
      }
    }
  }

  // Insert narrative for the final group (if any connectors remain)
  if (currentGroup !== null && parkNarrative[currentGroup]) {
    for (const text of parkNarrative[currentGroup]) {
      if (text) {
        result.push(new D.Paragraph({
          children: [new D.TextRun({ text, italics: true, color: '555555' })],
          spacing: { before: 160, after: 160 },
        }))
      }
    }
  }

  // End-of-day shows inline as checkbox items at the end of ride order
  if (endOfDayTip) {
    result.push(new D.Paragraph({ text: '', spacing: { after: 100 } }))
    // Split by period-space to render each event as a separate checkbox line
    // e.g. "El desfile nocturno es a las 7:45 pm... Ver el Show "Happily Ever After" a las 9:00 pm..."
    const events = endOfDayTip.split(/\.\s+/).filter(s => s.trim())
    for (const evt of events) {
      const text = evt.replace(/\.$/, '').trim()
      if (!text) continue
      result.push(new D.Paragraph({
        children: [
          new D.TextRun({ text: `[ ] `, bold: true }),
          new D.TextRun({ text, italics: true }),
        ],
        spacing: { after: 60 },
      }))
    }
  }

  return result
}

function buildEntertainmentTable(parkCode, config, D) {
  const result = []
  const liveEntertainment = config?._dateEntertainment  // Already resolved to this park+date
  const staticEntertainment = PARK_ENTERTAINMENT[parkCode]

  // Determine which data source to use
  const useLive = liveEntertainment?.length > 0
  let entertainmentItems

  if (useLive) {
    // Sort order: parades first, then shows, then fireworks, then characters
    const TYPE_ORDER = { parade: 0, show: 1, fireworks: 2, character: 3 }
    const sorted = [...liveEntertainment].sort((a, b) =>
      (TYPE_ORDER[a.type] ?? 1) - (TYPE_ORDER[b.type] ?? 1)
    )

    // Build items from live data, merging Sara's comments from static data via fuzzy name match
    entertainmentItems = sorted.map(live => {
      const timesStr = live.type === 'character'
        ? (live.hours || '')
        : (live.times || []).join(', ')

      // Find Sara's comment from static data by fuzzy name match
      let saraComment = ''
      if (staticEntertainment?.length) {
        const liveNameLower = live.name.toLowerCase()
        const match = staticEntertainment.find(s => {
          const staticNameLower = s.name.toLowerCase()
          return staticNameLower.includes(liveNameLower) || liveNameLower.includes(staticNameLower)
        })
        if (match?.sara) saraComment = match.sara
      }

      return {
        name: live.name,
        location: '', // live data may not have location
        times: timesStr,
        sara: saraComment,
      }
    })
  } else {
    // Fall back to static data
    entertainmentItems = staticEntertainment
  }

  if (!entertainmentItems?.length) return result

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'CONOCER PERSONAJES Y EVENTOS DURANTE EL DIA', bold: true, size: 24 })],
    spacing: { before: 300, after: 100 },
  }))

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: PARK_ENTERTAINMENT.disclaimer, italics: true, size: 18, color: '888888' })],
    spacing: { after: 100 },
  }))

  // Build table rows
  const PARK_COLORS = { mk: '1565C0', hs: 'C62828', ep: '6A1B9A', ak: '2E7D32' }
  const headerColor = PARK_COLORS[parkCode] || '333333'

  const headerRow = new D.TableRow({
    children: [
      new D.TableCell({
        children: [new D.Paragraph({ children: [new D.TextRun({ text: 'Entretenimiento', bold: true, size: 22, color: 'FFFFFF' })] })],
        width: { size: Math.round(TABLE_DXA * 60 / 100), type: D.WidthType.DXA },
        shading: { type: D.ShadingType?.SOLID || 'solid', color: headerColor },
      }),
      new D.TableCell({
        children: [new D.Paragraph({ children: [new D.TextRun({ text: 'Horario(s)', bold: true, size: 22, color: 'FFFFFF' })] })],
        width: { size: Math.round(TABLE_DXA * 40 / 100), type: D.WidthType.DXA },
        shading: { type: D.ShadingType?.SOLID || 'solid', color: headerColor },
      }),
    ],
  })

  const dataRows = entertainmentItems.map(item => {
    let nameText
    if (item.location && item.sara) {
      nameText = `${item.name} (${item.location}) — ${item.sara}`
    } else if (item.location) {
      nameText = `${item.name} (${item.location})`
    } else if (item.sara) {
      nameText = `${item.name} — ${item.sara}`
    } else {
      nameText = item.name
    }
    return new D.TableRow({
      children: [
        tableCell(nameText, D, false, 60),
        tableCell(item.times, D, false, 40),
      ],
    })
  })

  result.push(new D.Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: D.WidthType.PERCENTAGE },
    layout: D.TableLayoutType.FIXED,
    columnWidths: [6000, 4000],
  }))

  return result
}

function buildRestaurantTable(parkCode, restaurants, gr, diningPlanType, D) {
  const result = []
  const hasTableService = diningPlanType === 'standard'
  const hasAnyPlan = !!diningPlanType

  result.push(new D.Paragraph({
    text: 'Restaurantes Recomendados',
    heading: D.HeadingLevel.HEADING_3,
    spacing: { before: 200, after: 100 },
  }))

  // Dining plan context note
  if (hasTableService) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({
        text: '*Con su Disney Dining Plan tienen créditos Quick Service Y Table Service. Los restaurantes con mesero (*) usan su crédito Table Service — reserven con anticipación en My Disney Experience.',
        italics: true, color: '1565C0',
      })],
      spacing: { after: 150 },
    }))
  } else if (diningPlanType === 'quick_service') {
    result.push(new D.Paragraph({
      children: [new D.TextRun({
        text: '*Con su Quick Service Dining Plan, piden en mostrador sin reservación. Rápido y práctico — no pierden tiempo del parque esperando mesa.',
        italics: true, color: '1565C0',
      })],
      spacing: { after: 150 },
    }))
  }

  // Character dining note if they're interested
  if (gr.characterDining === 'si') {
    result.push(new D.Paragraph({
      children: [new D.TextRun({
        text: '🎭 Les interesa comer con personajes — pregúntenme y les ayudo a reservar un Character Dining cerca de este parque.',
        italics: true, color: '6B4EFF',
      })],
      spacing: { after: 150 },
    }))
  }

  // Dietary restrictions note
  if (gr.restricciones?.length) {
    const restr = gr.restricciones.join(', ')
    result.push(new D.Paragraph({
      children: [new D.TextRun({
        text: `OJO: Restricciones alimentarias: ${restr}. Todos los restaurantes de Disney/Universal manejan alergias — avisen al hacer el pedido.`,
        italics: true,
      })],
      spacing: { after: 150 },
    }))
  }

  // Filter restaurants by dining plan type
  // Quick Service plan or no plan: show only quick service restaurants
  // Disney Dining Plan (standard): show both quick and table service
  const filteredRestaurants = hasTableService
    ? restaurants
    : restaurants.filter(r => r.service !== 'table')

  // Restaurant table: Name (20%) | Location (15%) | Type (12%) | Comment (53%)
  const rows = [
    new D.TableRow({
      children: [
        tableCell('Restaurante', D, true, 20),
        tableCell('Ubicación', D, true, 15),
        tableCell('Tipo', D, true, 12),
        tableCell('Comentario', D, true, 53),
      ],
    }),
  ]

  // Sort: food preference matches first, then table service after quick service
  const foodPrefs = (gr.comida || []).map(f => f.toLowerCase())
  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    // Food preference matches first
    if (foodPrefs.length) {
      const aMatch = foodPrefs.some(p => a.type.toLowerCase().includes(p) || a.name.toLowerCase().includes(p))
      const bMatch = foodPrefs.some(p => b.type.toLowerCase().includes(p) || b.name.toLowerCase().includes(p))
      if (aMatch && !bMatch) return -1
      if (!aMatch && bMatch) return 1
    }
    // Quick service before table service
    if (a.service === 'quick' && b.service === 'table') return -1
    if (a.service === 'table' && b.service === 'quick') return 1
    return 0
  })

  // Add section divider between quick and table service when both present
  let addedTableHeader = false
  for (const r of sortedRestaurants) {
    if (hasTableService && r.service === 'table' && !addedTableHeader) {
      addedTableHeader = true
      // Table Service section header row
      rows.push(new D.TableRow({
        children: [
          tableCell('*Table Service (con reservación)', D, true, 35),
          tableCell('', D, true, 15),
          tableCell('', D, true, 12),
          tableCell('Usan 1 crédito Table Service', D, true, 38),
        ],
      }))
    }

    const isMatch = foodPrefs.length && foodPrefs.some(p => r.type.toLowerCase().includes(p) || r.name.toLowerCase().includes(p))
    const prefix = isMatch ? '*' : (r.service === 'table' ? '*' : '')
    rows.push(new D.TableRow({
      children: [
        tableCell(`${prefix}${r.name}`, D, false, 20),
        tableCell(r.location, D, false, 15),
        tableCell(r.type, D, false, 12),
        tableCell(r.sara, D, false, 53),
      ],
    }))
  }

  result.push(new D.Table({
    columnWidths: [Math.round(TABLE_DXA * 0.20), Math.round(TABLE_DXA * 0.15), Math.round(TABLE_DXA * 0.12), Math.round(TABLE_DXA * 0.53)],
    rows,
    width: { size: TABLE_DXA, type: D.WidthType.DXA },
    layout: D.TableLayoutType.FIXED,
  }))

  // Restaurant dishes and menu URLs (below the table)
  for (const r of sortedRestaurants) {
    if (r.dishes?.length || r.menuUrl) {
      result.push(new D.Paragraph({
        children: [new D.TextRun({ text: r.name, bold: true })],
        spacing: { before: 100, after: 40 },
      }))
      if (r.dishes?.length) {
        for (const dish of r.dishes) {
          result.push(new D.Paragraph({
            children: [new D.TextRun({ text: `  • ${dish}` })],
            spacing: { after: 20 },
          }))
        }
      }
      if (r.menuUrl) {
        result.push(new D.Paragraph({
          children: [
            new D.TextRun({ text: 'Menú: ', bold: true }),
            new D.TextRun({ text: r.menuUrl, color: '1565C0' }),
          ],
          spacing: { after: 60 },
        }))
      }
    }
  }

  return result
}

function buildEpicUniverse(D) {
  const result = []
  result.push(new D.Paragraph({
    text: `${EPIC_UNIVERSE.name} — Guía por Zona`,
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 300, after: 150 },
  }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: EPIC_UNIVERSE.arrivalTip, italics: true })],
    spacing: { after: 200 },
  }))

  for (const zone of EPIC_UNIVERSE.zones) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: zone.name, bold: true, size: 26 })],
      spacing: { before: 200, after: 60 },
    }))
    if (zone.sara) {
      result.push(new D.Paragraph({
        text: zone.sara,
        spacing: { after: 80 },
      }))
    }

    // Rides
    if (zone.rides?.length) {
      for (const r of zone.rides) {
        const parts = [new D.TextRun({ text: `[ ] ${r.name}`, bold: true })]
        if (r.sara) parts.push(new D.TextRun({ text: ` — ${r.sara}` }))
        result.push(new D.Paragraph({ children: parts, spacing: { after: 30 } }))
      }
    }

    // Shows
    if (zone.shows?.length) {
      for (const s of zone.shows) {
        const parts = [new D.TextRun({ text: `🎭 ${s.name}`, bold: true })]
        if (s.sara) parts.push(new D.TextRun({ text: ` — ${s.sara}` }))
        result.push(new D.Paragraph({ children: parts, spacing: { after: 30 } }))
      }
    }

    // Restaurants
    if (zone.restaurants?.length) {
      result.push(new D.Paragraph({
        children: [new D.TextRun({ text: 'Dónde comer:', bold: true, italics: true })],
        spacing: { before: 60, after: 30 },
      }))
      for (const rest of zone.restaurants) {
        const parts = [new D.TextRun({ text: `• ${rest.name}`, bold: true })]
        if (rest.sara) parts.push(new D.TextRun({ text: ` — ${rest.sara}` }))
        result.push(new D.Paragraph({ children: parts, spacing: { after: 30 } }))
      }
    }
  }

  // Upscale dining options
  if (EPIC_UNIVERSE.diningUpscale?.length) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: 'Si quieren cenar (servicio a la mesa)', bold: true, italics: true })],
      spacing: { before: 150, after: 60 },
    }))
    for (const r of EPIC_UNIVERSE.diningUpscale) {
      const parts = [new D.TextRun({ text: `• ${r.name}`, bold: true })]
      if (r.sara) parts.push(new D.TextRun({ text: ` — ${r.sara}` }))
      result.push(new D.Paragraph({ children: parts, spacing: { after: 30 } }))
    }
  }

  // Closing tip
  if (EPIC_UNIVERSE.closingTip) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: EPIC_UNIVERSE.closingTip, italics: true })],
      spacing: { before: 100, after: 100 },
    }))
  }

  return result
}

function buildCityWalk(gr, D) {
  const result = []
  result.push(new D.Paragraph({
    text: CITY_WALK.title,
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 100 },
  }))
  result.push(new D.Paragraph({ text: CITY_WALK.intro, spacing: { after: 200 } }))

  const foodPrefs = (gr.comida || []).map(f => f.toLowerCase())

  for (const r of CITY_WALK.restaurants) {
    const isMatch = foodPrefs.length && foodPrefs.some(p => r.type.toLowerCase().includes(p) || r.name.toLowerCase().includes(p))
    const parts = [new D.TextRun({ text: `• ${isMatch ? '*' : ''}${r.name}`, bold: true })]
    const desc = r.sara ? ` (${r.type}) — ${r.sara}` : ` (${r.type})`
    parts.push(new D.TextRun({ text: desc }))
    result.push(new D.Paragraph({ children: parts, spacing: { after: 40 } }))
  }
  return result
}

function buildMealPlan(lead, planTypeOverride, D) {
  const result = []
  result.push(new D.Paragraph({
    text: 'COMIDAS Y PLANES DE COMIDAS',
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 200 },
  }))
  result.push(new D.Paragraph({ text: MEAL_PLAN.intro, spacing: { after: 200 } }))

  // Use override from config modal, or detect from lead data
  const planType = planTypeOverride || detectMealPlanType(lead)
  const plan = MEAL_PLAN.types[planType] || MEAL_PLAN.types.quick_service

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: plan.name, bold: true })],
    spacing: { after: 80 },
  }))
  result.push(new D.Paragraph({ text: plan.perDay, spacing: { after: 80 } }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: plan.tip, italics: true })],
    spacing: { after: 150 },
  }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: `💡 ${MEAL_PLAN.snackTip}`, italics: true })],
    spacing: { after: 200 },
  }))

  // Hotel food court recommendations
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'COMIDAS EN EL HOTEL', bold: true })],
    spacing: { before: 200, after: 100 },
  }))

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'Desayuno (Quick Service):', bold: true })],
    spacing: { after: 60 },
  }))
  for (const item of HOTEL_DINING_RECS.breakfast) {
    result.push(new D.Paragraph({
      text: `• ${item}`,
      spacing: { after: 30 },
    }))
  }

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'Cena (Quick Service):', bold: true })],
    spacing: { before: 100, after: 60 },
  }))
  for (const item of HOTEL_DINING_RECS.dinner) {
    result.push(new D.Paragraph({
      text: `• ${item}`,
      spacing: { after: 30 },
    }))
  }

  result.push(new D.Paragraph({ text: '', spacing: { after: 80 } }))
  for (const tip of HOTEL_DINING_RECS.tips) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: tip, italics: true })],
      spacing: { after: 40 },
    }))
  }

  return result
}

function buildInfoAdicional(D) {
  const result = []
  result.push(new D.Paragraph({
    text: 'INFORMACIÓN ADICIONAL',
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 200 },
  }))

  // Contacts
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: INFO_ADICIONAL.contacts.title, bold: true })],
    spacing: { after: 100 },
  }))
  for (const c of INFO_ADICIONAL.contacts.items) {
    result.push(new D.Paragraph({
      children: [
        new D.TextRun({ text: `${c.name} ${c.phone}`, bold: true }),
        new D.TextRun({ text: ` ${c.role}` }),
      ],
      spacing: { after: 40 },
    }))
  }
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: INFO_ADICIONAL.contacts.note, italics: true })],
    spacing: { after: 200 },
  }))

  // Orlando activities
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: INFO_ADICIONAL.orlandoActivities.title, bold: true })],
    spacing: { after: 100 },
  }))
  for (const cat of INFO_ADICIONAL.orlandoActivities.categories) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: cat.name, bold: true, italics: true })],
      spacing: { before: 80, after: 40 },
    }))
    for (const item of cat.items) {
      result.push(new D.Paragraph({ text: `  • ${item}`, spacing: { after: 30 } }))
    }
  }

  // Essential tips
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: INFO_ADICIONAL.essentialTips.title, bold: true })],
    spacing: { before: 200, after: 100 },
  }))
  for (const tip of INFO_ADICIONAL.essentialTips.items) {
    result.push(new D.Paragraph({ text: `• ${tip}`, spacing: { after: 40 } }))
  }

  return result
}

function buildShopping(D) {
  const result = []
  result.push(new D.Paragraph({
    text: SHOPPING.title,
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 100 },
  }))
  result.push(new D.Paragraph({ text: SHOPPING.intro, spacing: { after: 200 } }))

  for (const outlet of SHOPPING.outlets) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: outlet.name, bold: true })],
      spacing: { after: 40 },
    }))
    result.push(new D.Paragraph({ text: outlet.address, spacing: { after: 40 } }))
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: outlet.tip, italics: true })],
      spacing: { after: 100 },
    }))
  }

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'Tips de compras:', bold: true })],
    spacing: { before: 100, after: 80 },
  }))
  for (const tip of SHOPPING.shoppingTips) {
    result.push(new D.Paragraph({ text: `• ${tip}`, spacing: { after: 40 } }))
  }
  return result
}

// ── PERSONALIZATION SUMMARY (Sara-facing) ─────────────────────────────────

function buildPersonalizationSummary(lead, gr, parks, diningPlanType, viajeros, D) {
  const result = []
  const PURPLE = '6B4EFF'

  // Page break before this section
  result.push(new D.Paragraph({
    children: [],
    pageBreakBefore: true,
  }))

  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'NOTAS DE PERSONALIZACIÓN — PARA SARA', bold: true, color: PURPLE, size: 28 })],
    spacing: { after: 60 },
  }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({ text: 'Esta página es para tu referencia — no va al cliente. Resume las respuestas del formulario y por qué se personalizó así.', italics: true, color: '888888', size: 18 })],
    spacing: { after: 200 },
  }))

  const addHeading = (text) => result.push(new D.Paragraph({
    children: [new D.TextRun({ text, bold: true, color: PURPLE, size: 22 })],
    spacing: { before: 200, after: 80 },
  }))
  const addLine = (text) => result.push(new D.Paragraph({
    text,
    spacing: { after: 40 },
  }))
  const addBullet = (text) => result.push(new D.Paragraph({
    text: `• ${text}`,
    spacing: { after: 30 },
  }))

  // 1. Viajeros (grupoViajeros uses `nombre`/`edad`/`esMenor` fields)
  addHeading('Viajeros')
  if (viajeros.length) {
    const adults = viajeros.filter(v => !v.esMenor)
    const minors = viajeros.filter(v => v.esMenor)
    const adultCount = adults.length || parseInt(gr.adultos) || lead.formData?.viajeros?.adultos || 0
    const minorCount = minors.length || parseInt(gr.menores) || lead.formData?.viajeros?.menores || 0
    let summary = `${adultCount} adulto${adultCount !== 1 ? 's' : ''}`
    if (minorCount > 0) {
      const ages = minors.map(m => `${m.nombre || 'menor'} (${m.edad})`).join(', ')
      summary += `, ${minorCount} menor${minorCount !== 1 ? 'es' : ''}: ${ages}`
    }
    addLine(summary)
    for (const v of viajeros) {
      addBullet(`${v.nombre || 'Sin nombre'}${v.edad !== undefined && v.edad !== null ? ` — ${v.edad} años` : ''}`)
    }
  } else {
    const adultCount = gr.adultos || lead.adultos || lead.formData?.adultos || ''
    const minorCount = gr.menores || lead.menores || lead.formData?.menores || '0'
    if (adultCount && adultCount !== '?') {
      addLine(`Adultos: ${adultCount} | Menores: ${minorCount}`)
    }
  }

  // 2. Ritmo
  addHeading('Ritmo del Viaje')
  const ritmoMap = { relajado: 'Relajado — menos atracciones, más descanso', normal: 'Normal — balance entre atracciones y descanso', intenso: 'Intenso — máximo de atracciones y experiencias' }
  addLine(ritmoMap[gr.ritmo] || gr.ritmo || 'No especificado')

  // 3. Food preferences
  if (gr.comida?.length || gr.restricciones?.length) {
    addHeading('Preferencias de Comida')
    if (gr.comida?.length) addLine(`Preferencias: ${gr.comida.join(', ')}`)
    if (gr.restricciones?.length) addLine(`Restricciones: ${gr.restricciones.join(', ')}`)
  }

  // 4. Dining plan
  addHeading('Plan de Comidas')
  const planNames = { none: 'Sin plan de comidas', quick_service: 'Quick Service Dining Plan', standard: 'Disney Dining Plan' }
  addLine(planNames[diningPlanType] || 'No especificado')
  if (diningPlanType === 'standard') addLine('→ Restaurantes Table Service incluidos en el itinerario')
  else if (diningPlanType === 'quick_service') addLine('→ Solo restaurantes Quick Service en el itinerario')

  // 5. Vision del viaje
  if (gr.visionViaje) {
    addHeading('Visión del Viaje')
    addLine(`"${gr.visionViaje}"`)
  }

  // 6. Celebrations
  if (gr.celebraciones?.length || gr.celebracionDetalle) {
    addHeading('Celebraciones')
    if (gr.celebraciones?.length) {
      for (const cel of gr.celebraciones) addBullet(cel)
    }
    if (gr.celebracionDetalle) addLine(`Detalle: ${gr.celebracionDetalle}`)
  }

  // 7. Lightning Lane choices per park
  const disneyParks = parks.filter(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  if (disneyParks.length) {
    addHeading('Lightning Lane por Parque')
    const llLabels = { no: 'No compró LL', multipass: 'Multi Pass', multipass_individual: 'Multi Pass + Individual' }
    for (const p of disneyParks) {
      const choice = gr[`ll_${p}`]
      const parkNames = { mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom' }
      addBullet(`${parkNames[p]}: ${llLabels[choice] || choice || 'No especificado'}`)
    }
  }

  // 8. Highlighted rides (selected by traveler)
  const allSelectedRides = []
  for (const p of parks) {
    const sel = gr[`atracciones_${p}`] || []
    if (sel.length) allSelectedRides.push({ park: p, rides: sel })
  }
  if (allSelectedRides.length) {
    addHeading('Atracciones Destacadas (elegidas por los viajeros)')
    const parkNames = { mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom', uso: 'Universal Studios', ioa: 'Islands of Adventure', epic: 'Epic Universe' }
    for (const { park, rides } of allSelectedRides) {
      addLine(`${parkNames[park] || park}:`)
      for (const r of rides) addBullet(prettifyFormKey(r))
    }
  }

  // 9. Shows selected per park
  const allSelectedShows = []
  for (const p of parks) {
    const sel = gr[`shows_${p}`] || []
    if (sel.length) allSelectedShows.push({ park: p, shows: sel })
  }
  if (allSelectedShows.length) {
    addHeading('Shows Preferidos')
    const parkNames = { mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom', uso: 'Universal Studios', ioa: 'Islands of Adventure', epic: 'Epic Universe' }
    for (const { park, shows } of allSelectedShows) {
      addLine(`${parkNames[park] || park}:`)
      for (const s of shows) addBullet(prettifyFormKey(s))
    }
  }

  // 10. End of day preference
  if (gr.finalDia) {
    addHeading('Final del Día')
    const finalMap = { hotel: 'Prefieren volver al hotel — no incluir shows nocturnos', parque: 'Se quedan para shows nocturnos y cierre del parque' }
    addLine(finalMap[gr.finalDia] || gr.finalDia)
  }

  // 11. Special experiences
  if (gr.experiencias?.length) {
    addHeading('Experiencias Especiales Solicitadas')
    for (const exp of gr.experiencias) addBullet(prettifyFormKey(exp))
  }

  // 12. Character dining
  if (gr.characterDining) {
    addHeading('Character Dining')
    addLine(gr.characterDining === 'si' ? 'Sí quieren character dining' : 'No les interesa character dining')
  }

  // 13. Client's existing reservations (#4)
  if (gr.reservacionesExistentes && gr.reservacionesDetalle) {
    addHeading('Reservaciones del cliente')
    addLine(gr.reservacionesDetalle)
  }

  return result
}

// ── GUIDE FORM INTEGRATION HELPERS ────────────────────────────────────────

// Splits rides into primary (selected / must-do) and secondary (if time permits)
// based on guide form selections and pace preference
function filterRides(allRides, selectedRides, hasGuideData, ritmo, ifTimePermits = []) {
  // Sara's rideOrder is the strategic backbone. Guide form selections:
  // 1. HIGHLIGHT favorites with *(_selected flag)
  // 2. BOOST selected rides from ifTimePermits/tail into the top 10
  const norm = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, '').trim()

  const matchesSelected = (ride, selectedNorm) => {
    const rideNorm = norm(ride.name)
    return selectedNorm.some(sel =>
      rideNorm.includes(sel) || sel.includes(rideNorm) ||
      (ride.key && sel.includes(norm(ride.key.replace(/_/g, ' '))))
    )
  }

  // Mark which rides the traveler selected as favorites
  if (hasGuideData && selectedRides.length) {
    const selectedNorm = selectedRides.map(norm)
    for (const ride of allRides) ride._selected = matchesSelected(ride, selectedNorm)
    for (const ride of ifTimePermits) ride._selected = matchesSelected(ride, selectedNorm)

    // Boost: pull selected rides from ifTimePermits into the main list's top 10
    const boosted = ifTimePermits.filter(r => r._selected)
    if (boosted.length > 0) {
      // Find selected rides in allRides that are beyond position 10
      const tailSelected = allRides.length > 10
        ? allRides.slice(10).filter(r => r._selected)
        : []

      // Build new order: Sara's top 10 + boosted from ifTimePermits + tail selected, capped at 10
      const saraTop = allRides.slice(0, 10)
      const saraRest = allRides.slice(10).filter(r => !r._selected) // non-selected tail
      const boostPool = [...boosted, ...tailSelected]

      // Insert boosted rides at end of top 10 (push unselected down)
      const combined = [...saraTop, ...boostPool]
      const primary = combined.slice(0, 10)
      const secondary = [...combined.slice(10), ...saraRest]
      // Remove boosted from ifTimePermits so they don't show twice
      const boostedNames = new Set(boosted.map(r => norm(r.name)))
      const remainingIfTime = ifTimePermits.filter(r => !boostedNames.has(norm(r.name)))

      if (ritmo === 'relajado') {
        const cutoff = Math.ceil(primary.length * 0.6)
        return { primary: primary.slice(0, cutoff), secondary: [...primary.slice(cutoff), ...secondary], skipped: [], ifTimePermitsFiltered: remainingIfTime }
      }
      return { primary, secondary, skipped: [], ifTimePermitsFiltered: remainingIfTime }
    }
  }

  if (ritmo === 'relajado') {
    const cutoff = Math.ceil(allRides.length * 0.6)
    return { primary: allRides.slice(0, cutoff), secondary: allRides.slice(cutoff), skipped: [], ifTimePermitsFiltered: ifTimePermits }
  }

  return { primary: allRides, secondary: [], skipped: [], ifTimePermitsFiltered: ifTimePermits }
}

// Filters shows based on guide form selections and end-of-day preference
function filterShows(allShows, selectedShows, hasGuideData, finalDia) {
  if (!hasGuideData || !selectedShows.length) {
    // No guide data — include all shows, but skip nighttime if they prefer hotel
    if (finalDia === 'hotel') {
      return allShows.filter(s => !isNighttimeShow(s))
    }
    return allShows
  }

  const norm = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, '').trim()
  const selectedNorm = selectedShows.map(norm)

  const filtered = allShows.filter(show => {
    const showNorm = norm(show.name)
    const isSelected = selectedNorm.some(sel => showNorm.includes(sel) || sel.includes(showNorm))
    // Always include nighttime shows if finalDia is 'shows'
    if (finalDia === 'shows' && isNighttimeShow(show)) return true
    // Exclude nighttime if they prefer hotel
    if (finalDia === 'hotel' && isNighttimeShow(show)) return false
    return isSelected || !hasGuideData
  })

  return filtered
}

function isNighttimeShow(show) {
  const name = show.name.toLowerCase()
  return name.includes('happily ever after') || name.includes('fantasmic') ||
    name.includes('luminous') || show.times?.includes('9:00 PM') ||
    show.times?.includes('8:00') || show.times?.includes('10:00')
}

// Detect if traveler prefers no roller coasters / extreme rides
// Checks visionViaje and any relevant guide form fields
function detectNoCoasters(gr) {
  if (!gr.visionViaje) return false
  const v = gr.visionViaje.toLowerCase()
  // Spanish keywords for "no coasters" / "no extreme rides"
  return v.includes('no montaña') || v.includes('no montañas') ||
    v.includes('sin montaña') || v.includes('sin montañas') ||
    v.includes('no subimos a montaña') || v.includes('no juegos extremos') ||
    v.includes('no extremos') || v.includes('no rusas') ||
    v.includes('sin rusas') || v.includes('no roller') ||
    v.includes('no coaster')
}


function buildPersonalNote(gr, familyName, D) {
  const result = []

  if (gr.visionViaje) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({
        text: `Familia ${familyName}, me contaron que lo que más quieren de este viaje es: "${gr.visionViaje}"`,
        italics: true,
      })],
      spacing: { before: 100, after: 100 },
    }))
    result.push(new D.Paragraph({
      text: 'Tengan eso en mente cada día — este itinerario está diseñado para que lo logren.',
      spacing: { after: 200 },
    }))
  }

  // Sara's internal notes — hidden in DOCX (1pt white text, invisible but preserved for re-parse)
  if (gr.notasSara) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: `{{M2M_NOTA_SARA:${gr.notasSara}}}`, size: 2, color: 'FFFFFF' })],
      spacing: { after: 0 },
    }))
  }

  return result
}

function buildCelebrations(gr, familyName, isDisney, isUniversal, D) {
  const result = []

  result.push(new D.Paragraph({
    text: 'Celebraciones Especiales',
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 300, after: 150 },
  }))

  const celebrationLabels = {
    cumpleanos: 'Cumpleaños',
    'cumpleaños': 'Cumpleaños',
    birthday: 'Cumpleaños',
    aniversario: 'Aniversario',
    anniversary: 'Aniversario',
    luna_de_miel: 'Luna de miel',
    honeymoon: 'Luna de miel',
    graduacion: 'Graduación',
    'graduación': 'Graduación',
    primera_vez: 'Primera vez en Disney/Universal',
  }

  // Auto-include "Primera vez" — Sara removes if not applicable
  const celebs = [...(gr.celebraciones || [])]
  if (!celebs.includes('primera_vez')) {
    celebs.unshift('primera_vez')
  }

  for (const cel of celebs) {
    let label = celebrationLabels[cel] || cel
    // Tailor label to destination
    if (cel === 'primera_vez') {
      if (isDisney && isUniversal) label = 'Primera vez en Disney y Universal'
      else if (isUniversal && !isDisney) label = 'Primera vez en Universal'
      else label = 'Primera vez en Disney'
    }
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: label, bold: true })],
      spacing: { after: 40 },
    }))
  }

  if (gr.celebracionDetalle) {
    result.push(new D.Paragraph({
      text: gr.celebracionDetalle,
      spacing: { after: 100 },
    }))
  }

  result.push(new D.Paragraph({
    text: 'En Disney pueden pedir un botón de celebración GRATIS en Guest Relations. Los personajes y empleados los felicitan todo el día — vale mucho la pena.',
    spacing: { after: 100 },
  }))
  result.push(new D.Paragraph({
    children: [new D.TextRun({
      text: 'Si quieren un pastel o decoración especial en el hotel o restaurante, avísenme con anticipación y lo coordino.',
      italics: true,
    })],
    spacing: { after: 200 },
  }))

  return result
}

function buildSpecialExperiences(gr, parks, D) {
  const result = []
  const isDisney = parks.some(p => ['mk', 'hs', 'ep', 'ak'].includes(p))

  result.push(new D.Paragraph({
    text: 'Experiencias Especiales',
    heading: D.HeadingLevel.HEADING_2,
    spacing: { before: 400, after: 150 },
  }))

  const experienceInfo = {
    bibbidi_bobbidi: {
      name: 'Bibbidi Bobbidi Boutique',
      desc: 'Transformación de princesa/caballero para los peques. Reserven con anticipación — se llena rápido. Ubicado en Magic Kingdom y Disney Springs.',
    },
    dessert_party: {
      name: 'Dessert Party',
      desc: 'Fiesta de postres con vista VIP a los fuegos artificiales. Hay que reservar. Es un gasto extra pero vale la pena si quieren una experiencia premium.',
    },
    after_hours: {
      name: 'After Hours Event',
      desc: 'Evento especial después del horario normal. Menos gente, filas cortísimas. Si coincide con su fecha, SE LOS RECOMIENDO.',
    },
    character_dining: {
      name: 'Character Dining',
      desc: 'Comer con personajes de Disney en la mesa. Los favoritos: Chef Mickey, Cinderella Royal Table, 1900 Park Fare. Reserven 60 días antes.',
    },
    behind_the_seeds: {
      name: 'Behind the Seeds Tour (EPCOT)',
      desc: 'Tour guiado por los invernaderos de Living with the Land. Educativo y diferente. Se reserva en el parque mismo.',
    },
  }

  for (const exp of gr.experiencias) {
    const info = experienceInfo[exp]
    if (info) {
      result.push(new D.Paragraph({
        children: [new D.TextRun({ text: `✨ ${info.name}`, bold: true })],
        spacing: { before: 100, after: 40 },
      }))
      result.push(new D.Paragraph({
        text: info.desc,
        spacing: { after: 100 },
      }))
    } else {
      // Unknown experience — convert raw token to human-readable label
      const label = prettifyFormKey(exp)
      result.push(new D.Paragraph({
        children: [new D.TextRun({ text: `* ${label}`, bold: true })],
        spacing: { before: 100, after: 100 },
      }))
    }
  }

  return result
}

// ── TABLE HELPER ─────────────────────────────────────────────────────────

// Table width in DXA (twentieths of a point). Letter page ~12240, minus 1440 margins = 9360 usable.
const TABLE_DXA = 9360

function tableCell(text, D, isHeader = false, widthPct = 25) {
  // DXA avoids docx npm bug where WidthType.PERCENTAGE appends "%" to numeric value
  const dxaValue = Math.round(TABLE_DXA * widthPct / 100)
  return new D.TableCell({
    children: [new D.Paragraph({
      children: [new D.TextRun({ text: text || '', bold: isHeader, size: isHeader ? 22 : 20 })],
    })],
    width: { size: dxaValue, type: D.WidthType.DXA },
    shading: isHeader ? { type: D.ShadingType?.SOLID || 'solid', color: 'E8E8E8' } : undefined,
  })
}

// ── DATA EXTRACTION HELPERS ──────────────────────────────────────────────

function extractFamilyName(lead) {
  const titular = lead.titular || ''
  // Take last word as family name (or full name if single word)
  const parts = titular.trim().split(/\s+/)
  return parts.length > 1 ? parts.slice(-1)[0] : titular
}

function getFirstHotel(lead) {
  if (lead.hotels?.length) return lead.hotels[0].name || 'el hotel'
  if (lead.formData?.hotelEspecifico) return lead.formData.hotelEspecifico
  return 'el hotel'
}

function getTravelStartDate(lead) {
  // First park day date from parkEntries, or travelDate, or first hotel check-in
  if (lead.parkEntries?.length) {
    const dates = lead.parkEntries.map(pe => pe.validFrom || pe.startDate).filter(Boolean)
    if (dates.length) return dates.sort()[0]
  }
  if (lead.travelDate) return lead.travelDate
  if (lead.hotels?.length) {
    const dates = lead.hotels.map(h => h.checkIn).filter(Boolean)
    if (dates.length) return dates.sort()[0]
  }
  return null
}

function detectParks(lead) {
  const parks = []
  const type = lead.type || ''
  const dest = (lead.destination || '').toLowerCase()
  const fd = lead.formData || {}

  // From parkEntries
  if (lead.parkEntries?.length) {
    for (const pe of lead.parkEntries) {
      const park = (pe.park || '').toLowerCase()
      if (park.includes('magic kingdom') || park.includes('mk')) parks.push('mk')
      if (park.includes('hollywood') || park.includes('hs')) parks.push('hs')
      if (park.includes('epcot') || park.includes('ep')) parks.push('ep')
      if (park.includes('animal kingdom') || park.includes('ak')) parks.push('ak')
      if (park.includes('universal studios') || park.includes('uso')) parks.push('uso')
      if (park.includes('islands of adventure') || park.includes('ioa')) parks.push('ioa')
      if (park.includes('epic universe') || park.includes('epic')) parks.push('epic')
    }
  }

  // From selectedParks in formData
  if (fd.selectedParks?.length) {
    for (const p of fd.selectedParks) {
      if (!parks.includes(p)) parks.push(p)
    }
  }

  // From guide form responses (selectedParks_disney / selectedParks_universal)
  const gr = fd.guideResponses || {}
  if (gr.selectedParks_disney?.length) {
    for (const p of gr.selectedParks_disney) {
      if (!parks.includes(p)) parks.push(p)
    }
  }
  if (gr.selectedParks_universal?.length) {
    for (const p of gr.selectedParks_universal) {
      if (!parks.includes(p)) parks.push(p)
    }
  }

  // Fallback: infer from type
  if (!parks.length) {
    if (type.includes('disney') || dest.includes('disney')) {
      parks.push('mk', 'hs', 'ep', 'ak')
    }
    if (type.includes('universal') || dest.includes('universal')) {
      parks.push('uso', 'ioa', 'epic')
    }
  }

  return [...new Set(parks)]
}

function detectDiningPlan(lead) {
  if (lead.parkEntries?.some(pe => pe.diningPlan)) return true
  if (lead.formData?.diningPlan) return true
  return false
}

function detectMealPlanType(lead) {
  const dp = lead.parkEntries?.find(pe => pe.diningPlan)?.diningPlan || lead.formData?.diningPlan || ''
  // Direct match for enum values
  if (['quick_service', 'standard'].includes(dp)) return dp
  if (dp === 'deluxe') return 'standard' // deluxe discontinued → treat as standard
  // Backward compat: boolean true → standard
  if (dp === true) return 'standard'
  // Text-based fallback
  const lower = String(dp).toLowerCase()
  if (lower.includes('deluxe') || lower.includes('standard') || lower.includes('table')) return 'standard'
  return 'quick_service'
}

// Check if an attraction name matches any closure in the park's closure list
function isClosedAttraction(rideName, parkClosures) {
  if (!parkClosures?.length) return false
  const rideNorm = rideName.toLowerCase().replace(/[^a-z0-9]/g, '')
  return parkClosures.some(c => {
    const closeNorm = c.name.toLowerCase().replace(/[^a-z0-9]/g, '')
    return rideNorm.includes(closeNorm) || closeNorm.includes(rideNorm)
  })
}

// Build a closures summary for rides that are under refurbishment but not in the ride list
function buildClosuresSummary(parkCode, parkClosures, D) {
  if (!parkClosures?.length) return []
  const result = []
  result.push(new D.Paragraph({
    children: [new D.TextRun({
      text: `🚧 Atracciones en mantenimiento al momento de generar este itinerario:`,
      bold: true, color: 'E65100',
    })],
    spacing: { before: 200, after: 80 },
  }))
  for (const c of parkClosures) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({
        text: `  • ${c.name} (${c.entity_type === 'SHOW' ? 'Show' : 'Atracción'})`,
        color: '999999', italics: true,
      })],
      spacing: { after: 30 },
    }))
  }
  result.push(new D.Paragraph({
    children: [new D.TextRun({
      text: 'Nota: Las atracciones en mantenimiento pueden cambiar. Verifica el estado actual en la app My Disney Experience o Universal Orlando.',
      italics: true, size: 18,
    })],
    spacing: { after: 150 },
  }))
  return result
}

function getParkName(code) {
  const names = {
    mk: 'Magic Kingdom',
    hs: 'Hollywood Studios',
    ep: 'EPCOT',
    ak: 'Animal Kingdom',
    uso: 'Universal Studios Orlando',
    ioa: 'Islands of Adventure',
    epic: 'Epic Universe',
  }
  return names[code] || code
}

function getEnhancedHours(day, parkCode, hotelInfo, parkHoursData) {
  const result = { display: day.hours || '', earlyEntry: null, extendedHours: null }

  // If hours already set from the schedule editor, parse them
  if (day.hours) {
    if (day.hours.includes('Early Entry')) result.earlyEntry = true
    if (day.hours.includes('Extended Evening') || day.hours.includes('Extended')) result.extendedHours = true
    result.display = day.hours
    return result
  }

  // Try to get hours from backend API data
  const dateKey = day.date || '' // YYYY-MM-DD
  const apiHours = parkHoursData?.parkHours?.[dateKey]?.[parkCode]
  if (!apiHours) return result

  const open = apiHours.open || '9:00'
  const close = apiHours.close || '21:00'
  const ee = apiHours.early_entry
  const ext = apiHours.extended_hours

  let display = `${open} – ${close}`

  const isDPark = ['mk', 'hs', 'ep', 'ak'].includes(parkCode)
  if (isDPark && hotelInfo.isDisneyGuest && ee) {
    display = `${ee} (Early Entry) – ${close}`
    result.earlyEntry = ee
  }
  if (isDPark && hotelInfo.disneyTier === 'deluxe' && ext) {
    const openPart = result.earlyEntry ? `${ee} (Early Entry)` : open
    display = `${openPart} – ${ext} (Extended Evening)`
    result.extendedHours = ext
  }

  result.display = display
  return result
}

function buildDefaultSchedule(lead, parks) {
  // Sara's recommended order: HS → AK → EP → MK (save MK for finale with fireworks)
  const SARA_ORDER = ['hs', 'ak', 'ep', 'mk', 'uso', 'ioa', 'epic']
  const sorted = [...parks].sort((a, b) => {
    const ai = SARA_ORDER.indexOf(a)
    const bi = SARA_ORDER.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  const startDate = lead.travelDate ? new Date(lead.travelDate + 'T12:00:00') : null
  return sorted.map((parkCode, i) => {
    let dateLabel = ''
    let date = ''
    if (startDate) {
      const d = new Date(startDate)
      d.setDate(d.getDate() + i + 1) // +1 to skip check-in day
      dateLabel = formatDateShort(d)
      date = d.toISOString().split('T')[0]
    }
    return { parkCode, dateLabel, date, hours: '' }
  })
}

function formatDateShort(dateInput) {
  try {
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
    if (isNaN(d.getTime())) return ''
    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`
  } catch {
    return ''
  }
}

// ── SECTION CONTENT EXTRACTION (for Supabase storage + diffing) ──────────

function extractSectionContent(paragraphs) {
  const sections = {}
  let currentSection = 'header'

  for (const p of paragraphs) {
    // Check for hidden markers
    const children = p.root?.children || p.children || []
    for (const child of children) {
      const text = child.text || child.root?.text || ''
      if (text.startsWith('{{M2M_SECTION:')) {
        currentSection = text.replace('{{M2M_SECTION:', '').replace('}}', '')
        sections[currentSection] = ''
        continue
      }
    }

    // Extract visible text
    const visibleText = children
      .map(c => c.text || c.root?.text || '')
      .filter(t => !t.startsWith('{{M2M_SECTION:'))
      .join('')

    if (visibleText.trim()) {
      sections[currentSection] = (sections[currentSection] || '') + visibleText + '\n'
    }
  }

  return sections
}

// ── FORM KEY → HUMAN NAME MAPPING ─────────────────────────────────────────

// Converts guide form internal keys like "guardians_of_galaxy" to "Guardians of the Galaxy"
function prettifyFormKey(key) {
  if (!key || typeof key !== 'string') return key || ''
  // Check EXPERIENCE_LABELS first for exact matches
  if (EXPERIENCE_LABELS[key]) return EXPERIENCE_LABELS[key]
  // If it already looks like a human name (has spaces, starts with uppercase), return as-is
  if (key.includes(' ') && key[0] === key[0].toUpperCase()) return key
  // Convert snake_case to Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bOf\b/g, 'of')
    .replace(/\bThe\b/g, 'the')
    .replace(/\bA\b/g, 'a')
    .replace(/\bAn\b/g, 'an')
    .replace(/\bAnd\b/g, 'and')
    .replace(/^./, c => c.toUpperCase()) // Ensure first char is uppercase
}

// ── REST DAY + GAP DAY HELPERS ───────────────────────────────────────────

function addOneDayISO(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  d.setDate(d.getDate() + 1)
  return d.toISOString().split('T')[0]
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  return `${dayNames[d.getDay()]} ${d.getDate()} ${monthNames[d.getMonth()]}`
}

function buildRestDay(lead, hotelInfo, dayDate, config, D) {
  const hotel = hotelInfo.hotelName || getFirstHotel(lead)
  const result = []

  // Date-specific heading: "Marzo 12: Descanso Hotel + Disney Springs"
  const dateLabel = dayDate ? formatDateLabel(dayDate) : ''
  const headingText = dateLabel
    ? `${dateLabel}: Descanso ${hotel} + Disney Springs`
    : `${REST_DAY.title} — ${hotel}`

  result.push(new D.Paragraph({
    text: headingText,
    heading: D.HeadingLevel.HEADING_1,
    spacing: { before: 400, after: 200 },
  }))

  for (const line of REST_DAY.template.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed) continue
    result.push(new D.Paragraph({
      text: trimmed,
      spacing: { after: 60 },
    }))
  }

  // Mini golf — Magical Extras for Disney hotel guests
  if (hotelInfo.isDisneyGuest && REST_DAY.miniGolf) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: REST_DAY.miniGolf, italics: true })],
      spacing: { before: 100, after: 100 },
    }))
  }

  // Shopping option
  if (REST_DAY.shoppingOption) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: '\nCompras:', bold: true })],
      spacing: { before: 150, after: 80 },
    }))
    for (const line of REST_DAY.shoppingOption.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed) continue
      result.push(new D.Paragraph({
        text: trimmed,
        spacing: { after: 40 },
      }))
    }
  }

  // Hotel dining
  if (REST_DAY.hotelDining) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: REST_DAY.hotelDining, italics: true })],
      spacing: { before: 100, after: 60 },
    }))
  }

  // Disney Springs reservations on rest day
  const dsReservations = (config?.diningReservations || []).filter(r =>
    r.parkCode === 'ds' && (!r.date || r.date === dayDate)
  )
  if (dsReservations.length) {
    result.push(new D.Paragraph({
      children: [new D.TextRun({ text: 'RESERVACIONES DEL DÍA:', bold: true })],
      spacing: { before: 150, after: 60 },
    }))
    for (const res of dsReservations) {
      result.push(new D.Paragraph({
        children: [
          new D.TextRun({ text: `• ${res.restaurant}`, bold: true }),
          new D.TextRun({ text: res.time ? ` a las ${res.time}` : '' }),
        ],
        spacing: { after: 30 },
      }))
    }
  }

  return result
}

// downloadBlob moved to itinerary-helpers.js

